/**
 * Hand-written GraphQL documents for operations whose fields aren't in the
 * committed `introspection.json` snapshot yet (the snapshot only refreshes
 * via `npm run codegen:schema` against a running api).
 *
 * Each document is a typed `TypedDocumentNode` literal that matches the shape
 * codegen would produce. The literal uses `as unknown as TypedDocumentNode<...>`
 * to coerce past graphql's strict `Kind` enum typing — same trick codegen
 * uses. Once the snapshot is refreshed and codegen runs, these definitions
 * should be removed and the generated `*Document` exports imported from
 * `@/shared/api/gql/graphql` instead.
  *
 * `BoardChangedDocument` remains only as a compatibility export for older
 * callers. The planner uses `stores/liveSync.ts`, whose subscription carries
 * an authoritative typed state snapshot instead of triggering queries. The
 * committed `introspection.json` snapshot pre-dates the subscription root,
 * so `graphql-codegen` cannot yet emit this legacy document. Regenerate the
 * snapshot (`npm run codegen:schema` with the api running) to unblock
 * codegen for this op too.
  */
import type { TypedDocumentNode } from '@graphql-typed-document-node/core'
import { parse } from 'graphql'

// ── Board ──────────────────────────────────────────────────────────────────

/**
 * Hand-written `board(guildId: Int!)` query.
 *
 * The codegen snapshot in `apollo/gql/graphql.ts` produces a `BoardDocument`
 * that uses the seaography-generated entity queries (`rooms(guildId:)`,
 * `groups(guildId:)`, `players(guildId:)`) — but those carry an N+1
 * `playerBuilds` join that hangs the request once any guild has more than
 * a handful of players. The custom `board(guildId:)` field in
 * `apps/backend/api/src/board.rs` is the batched, N+1-free replacement:
 * one round-trip for rooms, one for groups, one for players + their builds
 * via a `json_agg` subquery, with the `guildId` argument being the
 * **Discord snowflake** (not the internal db id — the server resolves
 * internally and authorises via the session's `memberFor` list).
 *
 * Until the codegen snapshot is regenerated against a running api
 * (`npm run codegen:schema && npm run codegen`), we override the generated
 * `BoardDocument` with this hand-written version. The store imports the
 * re-export below instead of the codegen one.
 */
export interface BoardQuery {
  readonly guildPlayerDiscordIds?: ReadonlyArray<string> | null
  board: {
    readonly rooms: ReadonlyArray<{
      readonly id: number
      readonly stableId: string
      readonly name: string
      readonly accent: string
      readonly noPlayerLimit: boolean
      readonly orderIndex: number
    }>
    readonly groups: ReadonlyArray<{
      readonly id: number
      readonly stableId: string
      readonly roomId: number
      readonly label: string
      readonly position: string
      readonly maxPlayers: number
      readonly collapsed: boolean
      readonly orderIndex: number
    }>
    readonly players: ReadonlyArray<{
      readonly player: {
        readonly id: number
        readonly discordUserId: string
        readonly hasSignedIn: boolean
        readonly discordName: string
        readonly guildNickname: string
        readonly playerNickname: string
        readonly playerAccountName: string
        readonly inGameName: string
        readonly role: string
        readonly groupId: number
        readonly groupOrderIndex: number
        readonly mainWeapons: ReadonlyArray<string>
        readonly altWeapons: ReadonlyArray<string>
        readonly activeBuildIndex: number
        readonly activeGuildBuildIndex: number
        /**
         * Tag assignments for this player, in the same shape as the
         * `playerTags` catalog (`{ id, guildId, name, seedKey, color }`). Plan 05
         * surfaces these as the chip strip on `PlayerCard` and the toolbar
         * Tags filter — without the selection they all render as `[]` and
         * the filter is a no-op. See `apps/backend/api/src/board.rs`
         * (`BoardPlayerTag`) for the wire shape.
         */
        readonly tags: ReadonlyArray<{
          readonly id: number
          readonly guildId: number
          readonly name: string
          readonly seedKey: string | null
          readonly color: string
        }>
      }
      readonly builds: ReadonlyArray<{
        readonly id: number
        readonly orderIndex: number
        readonly weaponSlugs: ReadonlyArray<string>
      }>
      readonly guildBuilds: ReadonlyArray<{
        readonly id: number
        readonly orderIndex: number
        readonly weaponSlugs: ReadonlyArray<string>
        readonly isAssigned: boolean
      }>
    }>
  }
}

export interface BoardQueryVariables {
  /**
   * Discord guild id (snowflake). The Apollo HttpLink in `@/shared/api/apolloClient` rewrites
   * string-form values to JSON numeric literals before the server sees
   * them — see `rewriteSnowflakes` in `apollo/index.ts`.
   */
  guildId: string
}

export const BoardDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'Board' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'guildId' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'Int' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'guildPlayerDiscordIds' },
            arguments: [{
              kind: 'Argument',
              name: { kind: 'Name', value: 'guildId' },
              value: { kind: 'Variable', name: { kind: 'Name', value: 'guildId' } },
            }],
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'board' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'guildId' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'guildId' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'rooms' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'stableId' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'accent' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'noPlayerLimit' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'orderIndex' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'groups' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'stableId' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'roomId' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'label' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'position' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'maxPlayers' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'collapsed' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'orderIndex' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'players' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'player' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'discordUserId' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'hasSignedIn' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'discordName' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'guildNickname' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'playerNickname' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'playerAccountName' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'inGameName' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'role' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'groupId' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'groupOrderIndex' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'mainWeapons' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'altWeapons' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'activeBuildIndex' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'activeGuildBuildIndex' } },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'tags' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'guildId' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'seedKey' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'color' } },
                                ],
                              },
                            },
                          ],
                        },
                      },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'builds' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'orderIndex' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'weaponSlugs' } },
                          ],
                        },
                      },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'guildBuilds' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'orderIndex' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'weaponSlugs' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'isAssigned' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as TypedDocumentNode<BoardQuery, BoardQueryVariables>

// ── RoomAdd ──────────────────────────────────────────────────────────────────

/**
 * Hand-written `roomAdd(guildId, name, accent)` mutation. Same reason as
 * `BoardDocument` above — not in the committed codegen snapshot, and the
 * store calls it on `+ Add room`. Returns the new room's id as a string
 * (the api returns i64; JS Number is fine for ids here because the new
 * row's id comes from a Postgres `bigserial`).
 */
export interface RoomAddMutation {
  roomAdd: string
}

export interface RoomAddMutationVariables {
  guildId: number
  name: string
  accent: string
}

export const RoomAddDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'RoomAdd' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'guildId' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'Int' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'name' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'String' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'accent' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'String' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'roomAdd' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'guildId' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'guildId' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'name' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'name' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'accent' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'accent' } },
              },
            ],
          },
        ],
      },
    },
  ],
} as unknown as TypedDocumentNode<RoomAddMutation, RoomAddMutationVariables>

// ── RoomRemove ─────────────────────────────────────────────────────────────

export interface RoomRemoveMutation {
  roomRemove: boolean
}

export interface RoomRemoveMutationVariables {
  guildId: number
  roomId: number
}

export const RoomRemoveDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'RoomRemove' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'guildId' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'Int' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'roomId' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'Int' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'roomRemove' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'guildId' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'guildId' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'roomId' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'roomId' } },
              },
            ],
          },
        ],
      },
    },
  ],
} as unknown as TypedDocumentNode<RoomRemoveMutation, RoomRemoveMutationVariables>

// ── MyGuilds ──────────────────────────────────────────────────────────────────

export interface MyGuildsQuery {
  readonly myGuilds: ReadonlyArray<{
    readonly id: number
    readonly discordGuildId: string | null
    readonly name: string
    readonly ownerDiscordId: string | null
    readonly accessRoleDiscordId: string | null
    readonly canView: boolean
    readonly accessRequested: boolean
    readonly language: string | null
    /** Null for the redacted `canView: false` request-access projection. */
    readonly isMember: boolean | null
    /** Null for the redacted `canView: false` request-access projection. */
    readonly isOfficer: boolean | null
    /** True only for the configured super admin; null for locked rows. */
    readonly canEditHistoricalRaids: boolean | null
  }>
}

export type MyGuildsQueryVariables = Record<string, never>

export const MyGuildsDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'MyGuilds' },
      variableDefinitions: [],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'myGuilds' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'discordGuildId' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'ownerDiscordId' } },
                { kind: 'Field', name: { kind: 'Name', value: 'accessRoleDiscordId' } },
                { kind: 'Field', name: { kind: 'Name', value: 'canView' } },
                { kind: 'Field', name: { kind: 'Name', value: 'accessRequested' } },
                { kind: 'Field', name: { kind: 'Name', value: 'language' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isMember' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isOfficer' } },
                { kind: 'Field', name: { kind: 'Name', value: 'canEditHistoricalRaids' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as TypedDocumentNode<MyGuildsQuery, MyGuildsQueryVariables>

// ── SetGuildOfficer ─────────────────────────────────────────────────────────

export interface SetGuildOfficerMutation {
  setGuildOfficer: boolean
}

export interface SetGuildOfficerMutationVariables {
  guildId: string
  discordUserId: string
  isOfficer: boolean
}

export const SetGuildOfficerDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'SetGuildOfficer' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'guildId' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'Int' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: {
            kind: 'Variable',
            name: { kind: 'Name', value: 'discordUserId' },
          },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'Int' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'isOfficer' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'Boolean' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'setGuildOfficer' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'guildId' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'guildId' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'discordUserId' },
                value: {
                  kind: 'Variable',
                  name: { kind: 'Name', value: 'discordUserId' },
                },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'isOfficer' },
                value: {
                  kind: 'Variable',
                  name: { kind: 'Name', value: 'isOfficer' },
                },
              },
            ],
          },
        ],
      },
    },
  ],
} as unknown as TypedDocumentNode<SetGuildOfficerMutation, SetGuildOfficerMutationVariables>

// ── BoardChanged ────────────────────────────────────────────────────────────

/**
 * Shape of a single `boardChanged` event. `entityId` is GraphQL `String`
 * because the event bus carries stringified IDs. New planner code does not
 * consume this legacy event shape; `payload` remains the raw
 * JSON blob so consumers can opt into parsing only when they need it.
 */
export interface BoardChangedSubscription {
  boardChanged: {
    readonly type: string
    readonly guildId: number
    readonly entityId: string | null
    readonly payload: string | null
  }
}

export interface BoardChangedSubscriptionVariables {
  /**
   * Internal `guilds.id` PK (NOT the Discord snowflake). The
   * `boardChanged` subscription's channel key is `wwm:guild:<id>:events`,
   * keyed on the DB PK, while the `board(guildId:)` query takes the
   * Discord snowflake. Different ids for different transports — see the
   * `subscriptions.rs::board_changed_field` resolver.
   */
  guildId: number
}

export const BoardChangedDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'subscription',
      name: { kind: 'Name', value: 'BoardChanged' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'guildId' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'Int' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'boardChanged' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'guildId' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'guildId' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'type' } },
                                { kind: 'Field', name: { kind: 'Name', value: 'guildId' } },
                                { kind: 'Field', name: { kind: 'Name', value: 'entityId' } },
                                { kind: 'Field', name: { kind: 'Name', value: 'payload' } },
                              ],
                            },
                          },
                        ],
                      },
                    },
                  ],
                } as unknown as TypedDocumentNode<BoardChangedSubscription, BoardChangedSubscriptionVariables>

                // ── RaidBoard ───────────────────────────────────────────────────────────────

                /**
                 * Hand-written `raidBoard(guildId, raidId, stageId)` query.
                 *
                 * Mirrors the codegen input in `../operations.graphql::query RaidBoard` but
                 * stays hand-written because the committed `introspection.json` snapshot
                 * pre-dates the raid schema; `graphql-codegen` errors out on `raidBoard`,
                 * `raidStages`, `raidsWindow` and friends and drops their `*Document`
                 * exports. Once `npm run codegen:schema` refreshes the snapshot, this whole
                 * block can be deleted and the store can import the generated document.
                 *
                 * ISS-04 — the `raid` selection MUST include `cancelledAt` and `completedAt`.
                 * `lib/raidCalendar.ts::raidStatus` derives the planner-facing `RaidStatus`
                 * from these two timestamps; without them the wire payload resolves both to
                 * `undefined`, the TS-only cast on `res.data` flows them through as `null`,
                 * and raid-mode rendering always reports `RaidStatus.Cancelled`. Keep the
                 * field selections here in lock-step with the `RaidBoardQuery.raid`
                 * interface below — the regression test `raidBoardDocument.test.ts` pins
                 * both sides together.
                 */
                export interface RaidBoardQuery {
                                  raidBoard: {
                                    readonly raid: {
                                      readonly id: number
                                      readonly guildId: number
                                      readonly name: string
                                      readonly isPublished: boolean
                                      readonly scheduledStartsAt: string | null
                                      readonly durationMinutes: number | null
                                      // ISS-04 — mirrored from `RaidBoardDocument`'s raid selection. The planner
                                      // derives `RaidStatus` from these two timestamps (see
                                      // `lib/raidCalendar.ts::raidStatus`); omitting them made raid-mode
                                      // rendering always return `Cancelled`.
                                      readonly cancelledAt: string | null
                                      readonly completedAt: string | null
                                      readonly sourceRaidId: number | null
                                      // Plan 06 — recurring-series metadata on the raid view. The
                                      // api derives the frequency from the spacing between the
                                      // first two instances; `seriesId` is the parent's PK so a
                                      // `WHERE recurrence_series_id = seriesId` query returns the
                                      // entire series including the parent.
                                      readonly recurrence: {
                                        readonly seriesId: number
                                        readonly parentId: number
                                        readonly index: number
                                        readonly total: number
                                        readonly frequency: string
                                      } | null
                                      readonly notes: string
                                      readonly createdBy: number | null
                                      readonly createdAt: string
                                      readonly updatedAt: string
                                    }
                    readonly selectedStageId: number | null
                    readonly stageDiffs: ReadonlyArray<{
                      readonly stageId: number
                      readonly playerId: number
                      readonly playerLabel: string
                      readonly placement: {
                        readonly beforeGroupId: number | null
                        readonly beforeOrderIndex: number
                        readonly afterGroupId: number
                        readonly afterOrderIndex: number
                      } | null
                      readonly build: {
                        readonly before: ReadonlyArray<{
                          readonly orderIndex: number
                          readonly weaponSlugs: ReadonlyArray<string>
                        }>
                        readonly after: ReadonlyArray<{
                          readonly orderIndex: number
                          readonly weaponSlugs: ReadonlyArray<string>
                        }>
                        readonly beforeActiveBuildIndex: number
                        readonly afterActiveBuildIndex: number
                      } | null
                      readonly tags: {
                        readonly added: ReadonlyArray<{
                          readonly id: number
                          readonly guildId: number
                          readonly name: string
                          readonly seedKey: string | null
                          readonly color: string
                        }>
                        readonly removed: ReadonlyArray<{
                          readonly id: number
                          readonly guildId: number
                          readonly name: string
                          readonly seedKey: string | null
                          readonly color: string
                        }>
                      } | null
                    }>
                    readonly rooms: ReadonlyArray<{
                      readonly id: number
                      readonly stableId: string
                      readonly guildId: number
                      readonly raidId: number
                      readonly stageId: number | null
                      readonly name: string
                      readonly accent: string
                      readonly noPlayerLimit: boolean
                      readonly orderIndex: number
                    }>
                    readonly groups: ReadonlyArray<{
                      readonly id: number
                      readonly stableId: string
                      readonly guildId: number
                      readonly raidId: number
                      readonly stageId: number | null
                      readonly roomId: number
                      readonly label: string
                      readonly position: string
                      readonly maxPlayers: number
                      readonly collapsed: boolean
                      readonly orderIndex: number
                    }>
                    readonly players: ReadonlyArray<{
                      readonly player: {
                        readonly id: number
                        readonly guildId: number
                        readonly raidId: number
                        readonly stageId: number | null
                        readonly playerId: number | null
                        readonly discordUserId: string
                        readonly hasSignedIn: boolean
                        readonly discordName: string
                        readonly guildNickname: string
                        readonly playerNickname: string
                        readonly playerAccountName: string
                        readonly inGameName: string
                        readonly role: string
                        readonly groupId: number
                        readonly groupOrderIndex: number
                        readonly mainWeapons: ReadonlyArray<string>
                        readonly altWeapons: ReadonlyArray<string>
                        readonly activeBuildIndex: number
                        readonly tags: ReadonlyArray<{
                          readonly id: number
                          readonly guildId: number
                          readonly name: string
                          readonly seedKey: string | null
                          readonly color: string
                        }>
                      }
                      readonly builds: ReadonlyArray<{
                        readonly id: number
                        readonly stageId: number | null
                        readonly orderIndex: number
                        readonly weaponSlugs: ReadonlyArray<string>
                      }>
                    }>
                  }
                }

                export interface RaidBoardQueryVariables {
                  /** Internal `guilds.id` PK, matching every other board-scoped query. */
                  guildId: number
                  raidId: number
                  /**
                   * Plan 04 — optional stage filter. `null` = cross-stage rows; non-null
                   * = rows for that stage + cross-stage rows so the Pool still renders.
                   */
                  stageId?: number | null
                }

                export const RaidBoardDocumentLegacy = {
                  kind: 'Document',
                  definitions: [
                    {
                      kind: 'OperationDefinition',
                      operation: 'query',
                      name: { kind: 'Name', value: 'RaidBoard' },
                      variableDefinitions: [
                        {
                          kind: 'VariableDefinition',
                          variable: { kind: 'Variable', name: { kind: 'Name', value: 'guildId' } },
                          type: {
                            kind: 'NonNullType',
                            type: { kind: 'NamedType', name: { kind: 'Name', value: 'Int' } },
                          },
                        },
                        {
                          kind: 'VariableDefinition',
                          variable: { kind: 'Variable', name: { kind: 'Name', value: 'raidId' } },
                          type: {
                            kind: 'NonNullType',
                            type: { kind: 'NamedType', name: { kind: 'Name', value: 'Int' } },
                          },
                        },
                        {
                          kind: 'VariableDefinition',
                          variable: { kind: 'Variable', name: { kind: 'Name', value: 'stageId' } },
                          type: { kind: 'NamedType', name: { kind: 'Name', value: 'Int' } },
                        },
                      ],
                      selectionSet: {
                        kind: 'SelectionSet',
                        selections: [
                          {
                            kind: 'Field',
                            name: { kind: 'Name', value: 'raidBoard' },
                            arguments: [
                              {
                                kind: 'Argument',
                                name: { kind: 'Name', value: 'guildId' },
                                value: { kind: 'Variable', name: { kind: 'Name', value: 'guildId' } },
                              },
                              {
                                kind: 'Argument',
                                name: { kind: 'Name', value: 'raidId' },
                                value: { kind: 'Variable', name: { kind: 'Name', value: 'raidId' } },
                              },
                              {
                                kind: 'Argument',
                                name: { kind: 'Name', value: 'stageId' },
                                value: { kind: 'Variable', name: { kind: 'Name', value: 'stageId' } },
                              },
                            ],
                            selectionSet: {
                              kind: 'SelectionSet',
                              selections: [
                                {
                                  kind: 'Field',
                                  name: { kind: 'Name', value: 'raid' },
                                  selectionSet: {
                                    kind: 'SelectionSet',
                                    selections: [
                                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                                      { kind: 'Field', name: { kind: 'Name', value: 'guildId' } },
                                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                                      { kind: 'Field', name: { kind: 'Name', value: 'isPublished' } },
                                      { kind: 'Field', name: { kind: 'Name', value: 'scheduledStartsAt' } },
                                      { kind: 'Field', name: { kind: 'Name', value: 'durationMinutes' } },
                                      // ISS-04 — keep these two selections together with the
                                      // `RaidBoardQuery.raid` interface above.
                                      { kind: 'Field', name: { kind: 'Name', value: 'cancelledAt' } },
                                      { kind: 'Field', name: { kind: 'Name', value: 'completedAt' } },
                                                                            { kind: 'Field', name: { kind: 'Name', value: 'sourceRaidId' } },
                                                                            // Plan 06 — `recurrence` selection mirrors the
                                                                            // `RaidBoardQuery.raid.recurrence` type above. Keep
                                                                            // both sides in lock-step.
                                                                            {
                                                                              kind: 'Field',
                                                                              name: { kind: 'Name', value: 'recurrence' },
                                                                              selectionSet: {
                                                                                kind: 'SelectionSet',
                                                                                selections: [
                                                                                  { kind: 'Field', name: { kind: 'Name', value: 'seriesId' } },
                                                                                  { kind: 'Field', name: { kind: 'Name', value: 'parentId' } },
                                                                                  { kind: 'Field', name: { kind: 'Name', value: 'index' } },
                                                                                  { kind: 'Field', name: { kind: 'Name', value: 'total' } },
                                                                                  { kind: 'Field', name: { kind: 'Name', value: 'frequency' } },
                                                                                ],
                                                                              },
                                                                            },
                                                                            { kind: 'Field', name: { kind: 'Name', value: 'notes' } },
                                                                            { kind: 'Field', name: { kind: 'Name', value: 'createdBy' } },
                                                                            { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
                                                                            { kind: 'Field', name: { kind: 'Name', value: 'updatedAt' } },
                                                                          ],
                                                                        },
                                                                      },
                                                                      {
                                                                        kind: 'Field',
                                                                        name: { kind: 'Name', value: 'rooms' },
                                  selectionSet: {
                                    kind: 'SelectionSet',
                                    selections: [
                                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                                      { kind: 'Field', name: { kind: 'Name', value: 'stableId' } },
                                      { kind: 'Field', name: { kind: 'Name', value: 'guildId' } },
                                      { kind: 'Field', name: { kind: 'Name', value: 'raidId' } },
                                      { kind: 'Field', name: { kind: 'Name', value: 'stageId' } },
                                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                                      { kind: 'Field', name: { kind: 'Name', value: 'accent' } },
                                      { kind: 'Field', name: { kind: 'Name', value: 'noPlayerLimit' } },
                                      { kind: 'Field', name: { kind: 'Name', value: 'orderIndex' } },
                                    ],
                                  },
                                },
                                {
                                  kind: 'Field',
                                  name: { kind: 'Name', value: 'groups' },
                                  selectionSet: {
                                    kind: 'SelectionSet',
                                    selections: [
                                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                                      { kind: 'Field', name: { kind: 'Name', value: 'stableId' } },
                                      { kind: 'Field', name: { kind: 'Name', value: 'guildId' } },
                                      { kind: 'Field', name: { kind: 'Name', value: 'raidId' } },
                                      { kind: 'Field', name: { kind: 'Name', value: 'stageId' } },
                                      { kind: 'Field', name: { kind: 'Name', value: 'roomId' } },
                                      { kind: 'Field', name: { kind: 'Name', value: 'label' } },
                                      { kind: 'Field', name: { kind: 'Name', value: 'position' } },
                                      { kind: 'Field', name: { kind: 'Name', value: 'maxPlayers' } },
                                      { kind: 'Field', name: { kind: 'Name', value: 'collapsed' } },
                                      { kind: 'Field', name: { kind: 'Name', value: 'orderIndex' } },
                                    ],
                                  },
                                },
                                {
                                  kind: 'Field',
                                  name: { kind: 'Name', value: 'players' },
                                  selectionSet: {
                                    kind: 'SelectionSet',
                                    selections: [
                                      {
                                        kind: 'Field',
                                        name: { kind: 'Name', value: 'player' },
                                        selectionSet: {
                                          kind: 'SelectionSet',
                                          selections: [
                                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                                            { kind: 'Field', name: { kind: 'Name', value: 'guildId' } },
                                            { kind: 'Field', name: { kind: 'Name', value: 'raidId' } },
                                            { kind: 'Field', name: { kind: 'Name', value: 'stageId' } },
                                            { kind: 'Field', name: { kind: 'Name', value: 'playerId' } },
                                            { kind: 'Field', name: { kind: 'Name', value: 'discordUserId' } },
                                            { kind: 'Field', name: { kind: 'Name', value: 'hasSignedIn' } },
                                            { kind: 'Field', name: { kind: 'Name', value: 'discordName' } },
                                            { kind: 'Field', name: { kind: 'Name', value: 'guildNickname' } },
                                            { kind: 'Field', name: { kind: 'Name', value: 'playerNickname' } },
                                            { kind: 'Field', name: { kind: 'Name', value: 'playerAccountName' } },
                                            { kind: 'Field', name: { kind: 'Name', value: 'inGameName' } },
                                            { kind: 'Field', name: { kind: 'Name', value: 'role' } },
                                            { kind: 'Field', name: { kind: 'Name', value: 'groupId' } },
                                            { kind: 'Field', name: { kind: 'Name', value: 'groupOrderIndex' } },
                                            { kind: 'Field', name: { kind: 'Name', value: 'mainWeapons' } },
                                            { kind: 'Field', name: { kind: 'Name', value: 'altWeapons' } },
                                            { kind: 'Field', name: { kind: 'Name', value: 'activeBuildIndex' } },
                                            {
                                              kind: 'Field',
                                              name: { kind: 'Name', value: 'tags' },
                                              selectionSet: {
                                                kind: 'SelectionSet',
                                                selections: [
                                                  { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                                                  { kind: 'Field', name: { kind: 'Name', value: 'guildId' } },
                                                  { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                                                  { kind: 'Field', name: { kind: 'Name', value: 'seedKey' } },
                                                  { kind: 'Field', name: { kind: 'Name', value: 'color' } },
                                                ],
                                              },
                                            },
                                          ],
                                        },
                                      },
                                      {
                                        kind: 'Field',
                                        name: { kind: 'Name', value: 'builds' },
                                        selectionSet: {
                                          kind: 'SelectionSet',
                                          selections: [
                                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                                            { kind: 'Field', name: { kind: 'Name', value: 'stageId' } },
                                            { kind: 'Field', name: { kind: 'Name', value: 'orderIndex' } },
                                            { kind: 'Field', name: { kind: 'Name', value: 'weaponSlugs' } },
                                          ],
                                        },
                                      },
                                    ],
                                  },
                                },
                              ],
                            },
                          },
                        ],
                      },
                    },
                  ],
                } as unknown as TypedDocumentNode<RaidBoardQuery, RaidBoardQueryVariables>

export const RaidBoardDocument = parse(`
  query RaidBoard($guildId: Int!, $raidId: Int!, $stageId: Int) {
    raidBoard(guildId: $guildId, raidId: $raidId, stageId: $stageId) {
      raid {
        id
        guildId
        name
        isPublished
        scheduledStartsAt
        durationMinutes
        cancelledAt
        completedAt
        sourceRaidId
        recurrence {
          seriesId
          parentId
          index
          total
          frequency
        }
        notes
        createdBy
        createdAt
        updatedAt
      }
      selectedStageId
      stageDiffs {
        stageId
        playerId
        playerLabel
        placement {
          beforeGroupId
          beforeOrderIndex
          afterGroupId
          afterOrderIndex
        }
        build {
          before {
            orderIndex
            weaponSlugs
          }
          after {
            orderIndex
            weaponSlugs
          }
          beforeActiveBuildIndex
          afterActiveBuildIndex
        }
        tags {
          added {
            id
            guildId
            name
            seedKey
            color
          }
          removed {
            id
            guildId
            name
            seedKey
            color
          }
        }
      }
      rooms {
        id
        stableId
        guildId
        raidId
        stageId
        name
        accent
        noPlayerLimit
        orderIndex
      }
      groups {
        id
        stableId
        guildId
        raidId
        stageId
        roomId
        label
        position
        maxPlayers
        collapsed
        orderIndex
      }
      players {
        player {
          id
          guildId
          raidId
          stageId
          playerId
          discordUserId
          hasSignedIn
          discordName
          guildNickname
          playerNickname
          playerAccountName
          inGameName
          role
          groupId
          groupOrderIndex
          mainWeapons
          altWeapons
          activeBuildIndex
          tags {
          id
          guildId
          name
          seedKey
          color
          }
        }
        builds {
          id
          stageId
          orderIndex
          weaponSlugs
        }
      }
    }
  }
`) as TypedDocumentNode<RaidBoardQuery, RaidBoardQueryVariables>

// ── Raids (plan 02) ────────────────────────────────────────────────────────

/**
* `raidsForGuild(guildId)` returns a stream of `RaidChangedEvent` JSON blobs.
* The store parses each `payload` string into a `RaidSummary` so the same
* mapper handles the `Raids` query and the live `raidChanged` subscription.
*/
export interface RaidsQuery {
raidsForGuild: ReadonlyArray<{
readonly type: string
readonly guildId: number
readonly raidId: number
readonly payload: string | null
}>
}

export interface RaidsQueryVariables {
guildId: number
}

export const RaidsDocument = {
kind: 'Document',
definitions: [
{
kind: 'OperationDefinition',
operation: 'query',
name: { kind: 'Name', value: 'Raids' },
variableDefinitions: [
{
kind: 'VariableDefinition',
variable: { kind: 'Variable', name: { kind: 'Name', value: 'guildId' } },
type: {
kind: 'NonNullType',
type: { kind: 'NamedType', name: { kind: 'Name', value: 'Int' } },
},
},
],
selectionSet: {
kind: 'SelectionSet',
selections: [
{
kind: 'Field',
name: { kind: 'Name', value: 'raidsForGuild' },
arguments: [
{
kind: 'Argument',
name: { kind: 'Name', value: 'guildId' },
value: { kind: 'Variable', name: { kind: 'Name', value: 'guildId' } },
},
],
selectionSet: {
kind: 'SelectionSet',
selections: [
{ kind: 'Field', name: { kind: 'Name', value: 'type' } },
{ kind: 'Field', name: { kind: 'Name', value: 'guildId' } },
{ kind: 'Field', name: { kind: 'Name', value: 'raidId' } },
{ kind: 'Field', name: { kind: 'Name', value: 'payload' } },
],
},
},
],
},
},
],
} as unknown as TypedDocumentNode<RaidsQuery, RaidsQueryVariables>

/**
* `raidsWindow(guildId, from, to)` returns the full `RaidSummary` rows
* for a date window. The store uses this for the calendar drawer.
*/
export interface RaidsWindowQuery {
raidsWindow: ReadonlyArray<{
readonly id: number
readonly guildId: number
readonly name: string
readonly isPublished: boolean
readonly scheduledStartsAt: string | null
readonly durationMinutes: number | null
readonly cancelledAt: string | null
readonly completedAt: string | null
readonly sourceRaidId: number | null
// Plan 06 — recurring-series metadata on the calendar window. The
// planner's `RaidCalendarView` chip renderer checks `recurrence !==
// null` to render the `↻ 3 / 8` badge. `total` is the per-series
// instance count the api derives in a single GROUP BY round-trip so the
// window fetch stays cheap.
readonly recurrence: {
readonly seriesId: number
readonly parentId: number
readonly index: number
readonly total: number
readonly frequency: string
} | null
readonly notes: string
readonly createdBy: number | null
readonly createdAt: string
readonly updatedAt: string
}>
}

export interface RaidsWindowQueryVariables {
guildId: number
from: string
to: string
}

export const RaidsWindowDocument = {
kind: 'Document',
definitions: [
{
kind: 'OperationDefinition',
operation: 'query',
name: { kind: 'Name', value: 'RaidsWindow' },
variableDefinitions: [
{
kind: 'VariableDefinition',
variable: { kind: 'Variable', name: { kind: 'Name', value: 'guildId' } },
type: {
kind: 'NonNullType',
type: { kind: 'NamedType', name: { kind: 'Name', value: 'Int' } },
},
},
{
kind: 'VariableDefinition',
variable: { kind: 'Variable', name: { kind: 'Name', value: 'from' } },
type: {
kind: 'NonNullType',
type: { kind: 'NamedType', name: { kind: 'Name', value: 'String' } },
},
},
{
kind: 'VariableDefinition',
variable: { kind: 'Variable', name: { kind: 'Name', value: 'to' } },
type: {
kind: 'NonNullType',
type: { kind: 'NamedType', name: { kind: 'Name', value: 'String' } },
},
},
],
selectionSet: {
kind: 'SelectionSet',
selections: [
{
kind: 'Field',
name: { kind: 'Name', value: 'raidsWindow' },
arguments: [
{
kind: 'Argument',
name: { kind: 'Name', value: 'guildId' },
value: { kind: 'Variable', name: { kind: 'Name', value: 'guildId' } },
},
{
kind: 'Argument',
name: { kind: 'Name', value: 'from' },
value: { kind: 'Variable', name: { kind: 'Name', value: 'from' } },
},
{
kind: 'Argument',
name: { kind: 'Name', value: 'to' },
value: { kind: 'Variable', name: { kind: 'Name', value: 'to' } },
},
],
selectionSet: {
kind: 'SelectionSet',
selections: [
{ kind: 'Field', name: { kind: 'Name', value: 'id' } },
{ kind: 'Field', name: { kind: 'Name', value: 'guildId' } },
{ kind: 'Field', name: { kind: 'Name', value: 'name' } },
{ kind: 'Field', name: { kind: 'Name', value: 'isPublished' } },
{ kind: 'Field', name: { kind: 'Name', value: 'scheduledStartsAt' } },
{ kind: 'Field', name: { kind: 'Name', value: 'durationMinutes' } },
{ kind: 'Field', name: { kind: 'Name', value: 'cancelledAt' } },
{ kind: 'Field', name: { kind: 'Name', value: 'completedAt' } },
{ kind: 'Field', name: { kind: 'Name', value: 'sourceRaidId' } },
// Plan 06 — `recurrence` selection mirrors the
// `RaidsWindowQuery.raidsWindow.recurrence` type above. Keep both
// sides in lock-step with `operations.graphql::query RaidsWindow`.
{
kind: 'Field',
name: { kind: 'Name', value: 'recurrence' },
selectionSet: {
kind: 'SelectionSet',
selections: [
{ kind: 'Field', name: { kind: 'Name', value: 'seriesId' } },
{ kind: 'Field', name: { kind: 'Name', value: 'parentId' } },
{ kind: 'Field', name: { kind: 'Name', value: 'index' } },
{ kind: 'Field', name: { kind: 'Name', value: 'total' } },
{ kind: 'Field', name: { kind: 'Name', value: 'frequency' } },
],
},
},
{ kind: 'Field', name: { kind: 'Name', value: 'notes' } },
{ kind: 'Field', name: { kind: 'Name', value: 'createdBy' } },
{ kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
{ kind: 'Field', name: { kind: 'Name', value: 'updatedAt' } },
],
},
},
],
},
},
],
} as unknown as TypedDocumentNode<RaidsWindowQuery, RaidsWindowQueryVariables>

/**
* `raidCreate(guildId, name, sourceRaidId?, scheduledStartsAt?, durationMinutes?, notes?)`.
* Returns the canonical `RaidChangedEvent` so the caller can read
* `data.raidCreate.raidId` straight off the mutation response (ISS-19).
*/
export interface RaidEventResponse {
readonly type: string
readonly guildId: number
readonly raidId: number | null
readonly payload: string | null
}

export interface RaidCreateMutation {
raidCreate: RaidEventResponse | null
}

export interface RaidCreateMutationVariables {
guildId: number
name: string
sourceRaidId?: number | null
scheduledStartsAt?: string | null
durationMinutes?: number | null
notes?: string | null
// Plan 06 — optional recurring-series input. When non-null, the api
// materialises `recurrence.count` raid rows sharing one series id and
// returns the parent's `raidId` (ISS-19). The calendar refetches the
// window after the mutation resolves so all instances land on the
// chips.
recurrence?: { readonly frequency: string; readonly count: number } | null
}

export const RaidCreateDocument = {
kind: 'Document',
definitions: [
{
kind: 'OperationDefinition',
operation: 'mutation',
name: { kind: 'Name', value: 'RaidCreate' },
variableDefinitions: [
{
kind: 'VariableDefinition',
variable: { kind: 'Variable', name: { kind: 'Name', value: 'guildId' } },
type: {
kind: 'NonNullType',
type: { kind: 'NamedType', name: { kind: 'Name', value: 'Int' } },
},
},
{
kind: 'VariableDefinition',
variable: { kind: 'Variable', name: { kind: 'Name', value: 'name' } },
type: {
kind: 'NonNullType',
type: { kind: 'NamedType', name: { kind: 'Name', value: 'String' } },
},
},
{
kind: 'VariableDefinition',
variable: { kind: 'Variable', name: { kind: 'Name', value: 'sourceRaidId' } },
type: { kind: 'NamedType', name: { kind: 'Name', value: 'Int' } },
},
{
kind: 'VariableDefinition',
variable: { kind: 'Variable', name: { kind: 'Name', value: 'scheduledStartsAt' } },
type: { kind: 'NamedType', name: { kind: 'Name', value: 'String' } },
},
{
kind: 'VariableDefinition',
variable: { kind: 'Variable', name: { kind: 'Name', value: 'durationMinutes' } },
type: { kind: 'NamedType', name: { kind: 'Name', value: 'Int' } },
},
{
kind: 'VariableDefinition',
variable: { kind: 'Variable', name: { kind: 'Name', value: 'notes' } },
type: { kind: 'NamedType', name: { kind: 'Name', value: 'String' } },
},
// Plan 06 — `recurrence: RaidRecurrenceInput` optional. Mirrors the
// `recurrence` field on `RaidCreateMutationVariables` above. Keep both
// sides in lock-step with `operations.graphql::mutation RaidCreate`.
{
kind: 'VariableDefinition',
variable: { kind: 'Variable', name: { kind: 'Name', value: 'recurrence' } },
type: { kind: 'NamedType', name: { kind: 'Name', value: 'RaidRecurrenceInput' } },
},
],
selectionSet: {
kind: 'SelectionSet',
selections: [
{
kind: 'Field',
name: { kind: 'Name', value: 'raidCreate' },
arguments: [
{
kind: 'Argument',
name: { kind: 'Name', value: 'guildId' },
value: { kind: 'Variable', name: { kind: 'Name', value: 'guildId' } },
},
{
kind: 'Argument',
name: { kind: 'Name', value: 'name' },
value: { kind: 'Variable', name: { kind: 'Name', value: 'name' } },
},
{
kind: 'Argument',
name: { kind: 'Name', value: 'sourceRaidId' },
value: { kind: 'Variable', name: { kind: 'Name', value: 'sourceRaidId' } },
},
{
kind: 'Argument',
name: { kind: 'Name', value: 'scheduledStartsAt' },
value: { kind: 'Variable', name: { kind: 'Name', value: 'scheduledStartsAt' } },
},
{
kind: 'Argument',
name: { kind: 'Name', value: 'durationMinutes' },
value: { kind: 'Variable', name: { kind: 'Name', value: 'durationMinutes' } },
},
{
kind: 'Argument',
name: { kind: 'Name', value: 'notes' },
value: { kind: 'Variable', name: { kind: 'Name', value: 'notes' } },
},
{
kind: 'Argument',
name: { kind: 'Name', value: 'recurrence' },
value: { kind: 'Variable', name: { kind: 'Name', value: 'recurrence' } },
},
],
selectionSet: {
kind: 'SelectionSet',
selections: [
{ kind: 'Field', name: { kind: 'Name', value: 'type' } },
{ kind: 'Field', name: { kind: 'Name', value: 'guildId' } },
{ kind: 'Field', name: { kind: 'Name', value: 'raidId' } },
{ kind: 'Field', name: { kind: 'Name', value: 'payload' } },
],
},
},
],
},
},
],
} as unknown as TypedDocumentNode<RaidCreateMutation, RaidCreateMutationVariables>

export interface RaidCloneMutation {
raidClone: RaidEventResponse
}

export interface RaidCloneMutationVariables {
guildId: number
raidId: number
newName: string
}

export const RaidCloneDocument = {
kind: 'Document',
definitions: [
{
kind: 'OperationDefinition',
operation: 'mutation',
name: { kind: 'Name', value: 'RaidClone' },
variableDefinitions: [
{
kind: 'VariableDefinition',
variable: { kind: 'Variable', name: { kind: 'Name', value: 'guildId' } },
type: {
kind: 'NonNullType',
type: { kind: 'NamedType', name: { kind: 'Name', value: 'Int' } },
},
},
{
kind: 'VariableDefinition',
variable: { kind: 'Variable', name: { kind: 'Name', value: 'raidId' } },
type: {
kind: 'NonNullType',
type: { kind: 'NamedType', name: { kind: 'Name', value: 'Int' } },
},
},
{
kind: 'VariableDefinition',
variable: { kind: 'Variable', name: { kind: 'Name', value: 'newName' } },
type: {
kind: 'NonNullType',
type: { kind: 'NamedType', name: { kind: 'Name', value: 'String' } },
},
},
],
selectionSet: {
kind: 'SelectionSet',
selections: [
{
kind: 'Field',
name: { kind: 'Name', value: 'raidClone' },
arguments: [
{
kind: 'Argument',
name: { kind: 'Name', value: 'guildId' },
value: { kind: 'Variable', name: { kind: 'Name', value: 'guildId' } },
},
{
kind: 'Argument',
name: { kind: 'Name', value: 'raidId' },
value: { kind: 'Variable', name: { kind: 'Name', value: 'raidId' } },
},
{
kind: 'Argument',
name: { kind: 'Name', value: 'newName' },
value: { kind: 'Variable', name: { kind: 'Name', value: 'newName' } },
},
],
selectionSet: {
kind: 'SelectionSet',
selections: [
{ kind: 'Field', name: { kind: 'Name', value: 'type' } },
{ kind: 'Field', name: { kind: 'Name', value: 'guildId' } },
{ kind: 'Field', name: { kind: 'Name', value: 'raidId' } },
{ kind: 'Field', name: { kind: 'Name', value: 'payload' } },
],
},
},
],
},
},
],
} as unknown as TypedDocumentNode<RaidCloneMutation, RaidCloneMutationVariables>

export interface RaidUpdateMutation {
raidUpdate: RaidEventResponse
}

export interface RaidUpdateMutationVariables {
guildId: number
raidId: number
name?: string | null
scheduledStartsAt?: string | null
durationMinutes?: number | null
notes?: string | null
}

export const RaidUpdateDocument = {
kind: 'Document',
definitions: [
{
kind: 'OperationDefinition',
operation: 'mutation',
name: { kind: 'Name', value: 'RaidUpdate' },
variableDefinitions: [
{
kind: 'VariableDefinition',
variable: { kind: 'Variable', name: { kind: 'Name', value: 'guildId' } },
type: {
kind: 'NonNullType',
type: { kind: 'NamedType', name: { kind: 'Name', value: 'Int' } },
},
},
{
kind: 'VariableDefinition',
variable: { kind: 'Variable', name: { kind: 'Name', value: 'raidId' } },
type: {
kind: 'NonNullType',
type: { kind: 'NamedType', name: { kind: 'Name', value: 'Int' } },
},
},
{
kind: 'VariableDefinition',
variable: { kind: 'Variable', name: { kind: 'Name', value: 'name' } },
type: { kind: 'NamedType', name: { kind: 'Name', value: 'String' } },
},
{
kind: 'VariableDefinition',
variable: { kind: 'Variable', name: { kind: 'Name', value: 'scheduledStartsAt' } },
type: { kind: 'NamedType', name: { kind: 'Name', value: 'String' } },
},
{
kind: 'VariableDefinition',
variable: { kind: 'Variable', name: { kind: 'Name', value: 'durationMinutes' } },
type: { kind: 'NamedType', name: { kind: 'Name', value: 'Int' } },
},
{
kind: 'VariableDefinition',
variable: { kind: 'Variable', name: { kind: 'Name', value: 'notes' } },
type: { kind: 'NamedType', name: { kind: 'Name', value: 'String' } },
},
],
selectionSet: {
kind: 'SelectionSet',
selections: [
{
kind: 'Field',
name: { kind: 'Name', value: 'raidUpdate' },
arguments: [
{
kind: 'Argument',
name: { kind: 'Name', value: 'guildId' },
value: { kind: 'Variable', name: { kind: 'Name', value: 'guildId' } },
},
{
kind: 'Argument',
name: { kind: 'Name', value: 'raidId' },
value: { kind: 'Variable', name: { kind: 'Name', value: 'raidId' } },
},
{
kind: 'Argument',
name: { kind: 'Name', value: 'name' },
value: { kind: 'Variable', name: { kind: 'Name', value: 'name' } },
},
{
kind: 'Argument',
name: { kind: 'Name', value: 'scheduledStartsAt' },
value: { kind: 'Variable', name: { kind: 'Name', value: 'scheduledStartsAt' } },
},
{
kind: 'Argument',
name: { kind: 'Name', value: 'durationMinutes' },
value: { kind: 'Variable', name: { kind: 'Name', value: 'durationMinutes' } },
},
{
kind: 'Argument',
name: { kind: 'Name', value: 'notes' },
value: { kind: 'Variable', name: { kind: 'Name', value: 'notes' } },
},
],
selectionSet: {
kind: 'SelectionSet',
selections: [
{ kind: 'Field', name: { kind: 'Name', value: 'type' } },
{ kind: 'Field', name: { kind: 'Name', value: 'guildId' } },
{ kind: 'Field', name: { kind: 'Name', value: 'raidId' } },
{ kind: 'Field', name: { kind: 'Name', value: 'payload' } },
],
},
},
],
},
},
],
} as unknown as TypedDocumentNode<RaidUpdateMutation, RaidUpdateMutationVariables>

export interface RaidSetPublishedMutation {
raidSetPublished: RaidEventResponse
}

export interface RaidSetPublishedMutationVariables {
guildId: number
raidId: number
isPublished: boolean
}

export const RaidSetPublishedDocument = parse(`
  mutation RaidSetPublished($guildId: Int!, $raidId: Int!, $isPublished: Boolean!) {
    raidSetPublished(guildId: $guildId, raidId: $raidId, isPublished: $isPublished) {
      type
      guildId
      raidId
      payload
    }
  }
`) as TypedDocumentNode<RaidSetPublishedMutation, RaidSetPublishedMutationVariables>

export interface RaidDeleteMutation {
raidDelete: boolean
}

export interface RaidDeleteMutationVariables {
guildId: number
raidId: number
}

export const RaidDeleteDocument = {
kind: 'Document',
definitions: [
{
kind: 'OperationDefinition',
operation: 'mutation',
name: { kind: 'Name', value: 'RaidDelete' },
variableDefinitions: [
{
kind: 'VariableDefinition',
variable: { kind: 'Variable', name: { kind: 'Name', value: 'guildId' } },
type: {
kind: 'NonNullType',
type: { kind: 'NamedType', name: { kind: 'Name', value: 'Int' } },
},
},
{
kind: 'VariableDefinition',
variable: { kind: 'Variable', name: { kind: 'Name', value: 'raidId' } },
type: {
kind: 'NonNullType',
type: { kind: 'NamedType', name: { kind: 'Name', value: 'Int' } },
},
},
],
selectionSet: {
kind: 'SelectionSet',
selections: [
{
kind: 'Field',
name: { kind: 'Name', value: 'raidDelete' },
arguments: [
{
kind: 'Argument',
name: { kind: 'Name', value: 'guildId' },
value: { kind: 'Variable', name: { kind: 'Name', value: 'guildId' } },
},
{
kind: 'Argument',
name: { kind: 'Name', value: 'raidId' },
value: { kind: 'Variable', name: { kind: 'Name', value: 'raidId' } },
},
],
},
],
},
},
],
} as unknown as TypedDocumentNode<RaidDeleteMutation, RaidDeleteMutationVariables>

export interface RaidCancelMutation {
raidCancel: boolean
}

export interface RaidCancelMutationVariables {
guildId: number
raidId: number
}

export const RaidCancelDocument = {
kind: 'Document',
definitions: [
{
kind: 'OperationDefinition',
operation: 'mutation',
name: { kind: 'Name', value: 'RaidCancel' },
variableDefinitions: [
{
kind: 'VariableDefinition',
variable: { kind: 'Variable', name: { kind: 'Name', value: 'guildId' } },
type: {
kind: 'NonNullType',
type: { kind: 'NamedType', name: { kind: 'Name', value: 'Int' } },
},
},
{
kind: 'VariableDefinition',
variable: { kind: 'Variable', name: { kind: 'Name', value: 'raidId' } },
type: {
kind: 'NonNullType',
type: { kind: 'NamedType', name: { kind: 'Name', value: 'Int' } },
},
},
],
selectionSet: {
kind: 'SelectionSet',
selections: [
{
kind: 'Field',
name: { kind: 'Name', value: 'raidCancel' },
arguments: [
{
kind: 'Argument',
name: { kind: 'Name', value: 'guildId' },
value: { kind: 'Variable', name: { kind: 'Name', value: 'guildId' } },
},
{
kind: 'Argument',
name: { kind: 'Name', value: 'raidId' },
value: { kind: 'Variable', name: { kind: 'Name', value: 'raidId' } },
},
],
},
],
},
},
],
} as unknown as TypedDocumentNode<RaidCancelMutation, RaidCancelMutationVariables>

export interface RaidSaveAsTemplateMutation {
raidSaveAsTemplate: RaidEventResponse
}

export interface RaidSaveAsTemplateMutationVariables {
guildId: number
raidId: number
}

export const RaidSaveAsTemplateDocument = {
kind: 'Document',
definitions: [
{
kind: 'OperationDefinition',
operation: 'mutation',
name: { kind: 'Name', value: 'RaidSaveAsTemplate' },
variableDefinitions: [
{
kind: 'VariableDefinition',
variable: { kind: 'Variable', name: { kind: 'Name', value: 'guildId' } },
type: {
kind: 'NonNullType',
type: { kind: 'NamedType', name: { kind: 'Name', value: 'Int' } },
},
},
{
kind: 'VariableDefinition',
variable: { kind: 'Variable', name: { kind: 'Name', value: 'raidId' } },
type: {
kind: 'NonNullType',
type: { kind: 'NamedType', name: { kind: 'Name', value: 'Int' } },
},
},
],
selectionSet: {
kind: 'SelectionSet',
selections: [
{
kind: 'Field',
name: { kind: 'Name', value: 'raidSaveAsTemplate' },
arguments: [
{
kind: 'Argument',
name: { kind: 'Name', value: 'guildId' },
value: { kind: 'Variable', name: { kind: 'Name', value: 'guildId' } },
},
{
kind: 'Argument',
name: { kind: 'Name', value: 'raidId' },
value: { kind: 'Variable', name: { kind: 'Name', value: 'raidId' } },
},
],
selectionSet: {
kind: 'SelectionSet',
selections: [
{ kind: 'Field', name: { kind: 'Name', value: 'type' } },
{ kind: 'Field', name: { kind: 'Name', value: 'guildId' } },
{ kind: 'Field', name: { kind: 'Name', value: 'raidId' } },
{ kind: 'Field', name: { kind: 'Name', value: 'payload' } },
],
},
},
],
},
},
],
} as unknown as TypedDocumentNode<RaidSaveAsTemplateMutation, RaidSaveAsTemplateMutationVariables>

export interface RaidApplyToBoardMutation {
raidApplyToBoard: boolean
}

export interface RaidApplyToBoardMutationVariables {
guildId: number
raidId: number
}

export const RaidApplyToBoardDocument = {
kind: 'Document',
definitions: [
{
kind: 'OperationDefinition',
operation: 'mutation',
name: { kind: 'Name', value: 'RaidApplyToBoard' },
variableDefinitions: [
{
kind: 'VariableDefinition',
variable: { kind: 'Variable', name: { kind: 'Name', value: 'guildId' } },
type: {
kind: 'NonNullType',
type: { kind: 'NamedType', name: { kind: 'Name', value: 'Int' } },
},
},
{
kind: 'VariableDefinition',
variable: { kind: 'Variable', name: { kind: 'Name', value: 'raidId' } },
type: {
kind: 'NonNullType',
type: { kind: 'NamedType', name: { kind: 'Name', value: 'Int' } },
},
},
],
selectionSet: {
kind: 'SelectionSet',
selections: [
{
kind: 'Field',
name: { kind: 'Name', value: 'raidApplyToBoard' },
arguments: [
{
kind: 'Argument',
name: { kind: 'Name', value: 'guildId' },
value: { kind: 'Variable', name: { kind: 'Name', value: 'guildId' } },
},
{
kind: 'Argument',
name: { kind: 'Name', value: 'raidId' },
value: { kind: 'Variable', name: { kind: 'Name', value: 'raidId' } },
},
],
},
],
},
},
],
} as unknown as TypedDocumentNode<RaidApplyToBoardMutation, RaidApplyToBoardMutationVariables>

// ── Raid stages (plan 04) ──────────────────────────────────────────────────

/**
* `raidStagesForRaid(guildId, raidId)` returns a stream of `StageChangedEvent`
* JSON blobs. The store parses each `payload` into a `RaidStage`.
*/
export interface RaidStagesQuery {
raidStagesForRaid: ReadonlyArray<{
readonly type: string
readonly guildId: number
readonly raidId: number
readonly payload: string | null
}>
}

export interface RaidStagesQueryVariables {
guildId: number
raidId: number
}

export const RaidStagesDocument = {
kind: 'Document',
definitions: [
{
kind: 'OperationDefinition',
operation: 'query',
name: { kind: 'Name', value: 'RaidStages' },
variableDefinitions: [
{
kind: 'VariableDefinition',
variable: { kind: 'Variable', name: { kind: 'Name', value: 'guildId' } },
type: {
kind: 'NonNullType',
type: { kind: 'NamedType', name: { kind: 'Name', value: 'Int' } },
},
},
{
kind: 'VariableDefinition',
variable: { kind: 'Variable', name: { kind: 'Name', value: 'raidId' } },
type: {
kind: 'NonNullType',
type: { kind: 'NamedType', name: { kind: 'Name', value: 'Int' } },
},
},
],
selectionSet: {
kind: 'SelectionSet',
selections: [
{
kind: 'Field',
name: { kind: 'Name', value: 'raidStagesForRaid' },
arguments: [
{
kind: 'Argument',
name: { kind: 'Name', value: 'guildId' },
value: { kind: 'Variable', name: { kind: 'Name', value: 'guildId' } },
},
{
kind: 'Argument',
name: { kind: 'Name', value: 'raidId' },
value: { kind: 'Variable', name: { kind: 'Name', value: 'raidId' } },
},
],
selectionSet: {
kind: 'SelectionSet',
selections: [
{ kind: 'Field', name: { kind: 'Name', value: 'type' } },
{ kind: 'Field', name: { kind: 'Name', value: 'guildId' } },
{ kind: 'Field', name: { kind: 'Name', value: 'raidId' } },
{ kind: 'Field', name: { kind: 'Name', value: 'payload' } },
],
},
},
],
},
},
],
} as unknown as TypedDocumentNode<RaidStagesQuery, RaidStagesQueryVariables>

export interface RaidStageAddMutation {
raidStageAdd: {
readonly type: string
readonly guildId: number
readonly raidId: number
readonly payload: string | null
} | null
}

export interface RaidStageAddMutationVariables {
guildId: number
raidId: number
kind: string
name?: string | null
notes?: string | null
}

export const RaidStageAddDocument = {
kind: 'Document',
definitions: [
{
kind: 'OperationDefinition',
operation: 'mutation',
name: { kind: 'Name', value: 'RaidStageAdd' },
variableDefinitions: [
{
kind: 'VariableDefinition',
variable: { kind: 'Variable', name: { kind: 'Name', value: 'guildId' } },
type: {
kind: 'NonNullType',
type: { kind: 'NamedType', name: { kind: 'Name', value: 'Int' } },
},
},
{
kind: 'VariableDefinition',
variable: { kind: 'Variable', name: { kind: 'Name', value: 'raidId' } },
type: {
kind: 'NonNullType',
type: { kind: 'NamedType', name: { kind: 'Name', value: 'Int' } },
},
},
{
kind: 'VariableDefinition',
variable: { kind: 'Variable', name: { kind: 'Name', value: 'kind' } },
type: {
kind: 'NonNullType',
type: { kind: 'NamedType', name: { kind: 'Name', value: 'String' } },
},
},
{
kind: 'VariableDefinition',
variable: { kind: 'Variable', name: { kind: 'Name', value: 'name' } },
type: { kind: 'NamedType', name: { kind: 'Name', value: 'String' } },
},
{
kind: 'VariableDefinition',
variable: { kind: 'Variable', name: { kind: 'Name', value: 'notes' } },
type: { kind: 'NamedType', name: { kind: 'Name', value: 'String' } },
},
],
selectionSet: {
kind: 'SelectionSet',
selections: [
{
kind: 'Field',
name: { kind: 'Name', value: 'raidStageAdd' },
arguments: [
{
kind: 'Argument',
name: { kind: 'Name', value: 'guildId' },
value: { kind: 'Variable', name: { kind: 'Name', value: 'guildId' } },
},
{
kind: 'Argument',
name: { kind: 'Name', value: 'raidId' },
value: { kind: 'Variable', name: { kind: 'Name', value: 'raidId' } },
},
{
kind: 'Argument',
name: { kind: 'Name', value: 'kind' },
value: { kind: 'Variable', name: { kind: 'Name', value: 'kind' } },
},
{
kind: 'Argument',
name: { kind: 'Name', value: 'name' },
value: { kind: 'Variable', name: { kind: 'Name', value: 'name' } },
},
{
kind: 'Argument',
name: { kind: 'Name', value: 'notes' },
value: { kind: 'Variable', name: { kind: 'Name', value: 'notes' } },
},
],
selectionSet: {
kind: 'SelectionSet',
selections: [
{ kind: 'Field', name: { kind: 'Name', value: 'type' } },
{ kind: 'Field', name: { kind: 'Name', value: 'guildId' } },
{ kind: 'Field', name: { kind: 'Name', value: 'raidId' } },
{ kind: 'Field', name: { kind: 'Name', value: 'payload' } },
],
},
},
],
},
},
],
} as unknown as TypedDocumentNode<RaidStageAddMutation, RaidStageAddMutationVariables>

export interface RaidStageUpdateMutation {
raidStageUpdate: {
readonly type: string
readonly guildId: number
readonly raidId: number
readonly payload: string | null
} | null
}

export interface RaidStageUpdateMutationVariables {
guildId: number
raidId: number
stageId: number
kind?: string | null
name?: string | null
notes?: string | null
}

export const RaidStageUpdateDocument = {
kind: 'Document',
definitions: [
{
kind: 'OperationDefinition',
operation: 'mutation',
name: { kind: 'Name', value: 'RaidStageUpdate' },
variableDefinitions: [
{
kind: 'VariableDefinition',
variable: { kind: 'Variable', name: { kind: 'Name', value: 'guildId' } },
type: {
kind: 'NonNullType',
type: { kind: 'NamedType', name: { kind: 'Name', value: 'Int' } },
},
},
{
kind: 'VariableDefinition',
variable: { kind: 'Variable', name: { kind: 'Name', value: 'raidId' } },
type: {
kind: 'NonNullType',
type: { kind: 'NamedType', name: { kind: 'Name', value: 'Int' } },
},
},
{
kind: 'VariableDefinition',
variable: { kind: 'Variable', name: { kind: 'Name', value: 'stageId' } },
type: {
kind: 'NonNullType',
type: { kind: 'NamedType', name: { kind: 'Name', value: 'Int' } },
},
},
{
kind: 'VariableDefinition',
variable: { kind: 'Variable', name: { kind: 'Name', value: 'kind' } },
type: { kind: 'NamedType', name: { kind: 'Name', value: 'String' } },
},
{
kind: 'VariableDefinition',
variable: { kind: 'Variable', name: { kind: 'Name', value: 'name' } },
type: { kind: 'NamedType', name: { kind: 'Name', value: 'String' } },
},
{
kind: 'VariableDefinition',
variable: { kind: 'Variable', name: { kind: 'Name', value: 'notes' } },
type: { kind: 'NamedType', name: { kind: 'Name', value: 'String' } },
},
],
selectionSet: {
kind: 'SelectionSet',
selections: [
{
kind: 'Field',
name: { kind: 'Name', value: 'raidStageUpdate' },
arguments: [
{
kind: 'Argument',
name: { kind: 'Name', value: 'guildId' },
value: { kind: 'Variable', name: { kind: 'Name', value: 'guildId' } },
},
{
kind: 'Argument',
name: { kind: 'Name', value: 'raidId' },
value: { kind: 'Variable', name: { kind: 'Name', value: 'raidId' } },
},
{
kind: 'Argument',
name: { kind: 'Name', value: 'stageId' },
value: { kind: 'Variable', name: { kind: 'Name', value: 'stageId' } },
},
{
kind: 'Argument',
name: { kind: 'Name', value: 'kind' },
value: { kind: 'Variable', name: { kind: 'Name', value: 'kind' } },
},
{
kind: 'Argument',
name: { kind: 'Name', value: 'name' },
value: { kind: 'Variable', name: { kind: 'Name', value: 'name' } },
},
{
kind: 'Argument',
name: { kind: 'Name', value: 'notes' },
value: { kind: 'Variable', name: { kind: 'Name', value: 'notes' } },
},
],
selectionSet: {
kind: 'SelectionSet',
selections: [
{ kind: 'Field', name: { kind: 'Name', value: 'type' } },
{ kind: 'Field', name: { kind: 'Name', value: 'guildId' } },
{ kind: 'Field', name: { kind: 'Name', value: 'raidId' } },
{ kind: 'Field', name: { kind: 'Name', value: 'payload' } },
],
},
},
],
},
},
],
} as unknown as TypedDocumentNode<RaidStageUpdateMutation, RaidStageUpdateMutationVariables>

export interface RaidStageReorderMutation {
readonly raidStageReorder: boolean
}

export interface RaidStageReorderMutationVariables {
guildId: number
raidId: number
orderedStageIds: ReadonlyArray<number>
}

export const RaidStageReorderDocument = {
kind: 'Document',
definitions: [
{
kind: 'OperationDefinition',
operation: 'mutation',
name: { kind: 'Name', value: 'RaidStageReorder' },
variableDefinitions: [
{
kind: 'VariableDefinition',
variable: { kind: 'Variable', name: { kind: 'Name', value: 'guildId' } },
type: {
kind: 'NonNullType',
type: { kind: 'NamedType', name: { kind: 'Name', value: 'Int' } },
},
},
{
kind: 'VariableDefinition',
variable: { kind: 'Variable', name: { kind: 'Name', value: 'raidId' } },
type: {
kind: 'NonNullType',
type: { kind: 'NamedType', name: { kind: 'Name', value: 'Int' } },
},
},
{
kind: 'VariableDefinition',
variable: { kind: 'Variable', name: { kind: 'Name', value: 'orderedStageIds' } },
type: {
kind: 'NonNullType',
type: {
kind: 'ListType',
type: {
kind: 'NonNullType',
type: { kind: 'NamedType', name: { kind: 'Name', value: 'Int' } },
},
},
},
},
],
selectionSet: {
kind: 'SelectionSet',
selections: [
{
kind: 'Field',
name: { kind: 'Name', value: 'raidStageReorder' },
arguments: [
{
kind: 'Argument',
name: { kind: 'Name', value: 'guildId' },
value: { kind: 'Variable', name: { kind: 'Name', value: 'guildId' } },
},
{
kind: 'Argument',
name: { kind: 'Name', value: 'raidId' },
value: { kind: 'Variable', name: { kind: 'Name', value: 'raidId' } },
},
{
kind: 'Argument',
name: { kind: 'Name', value: 'orderedStageIds' },
value: { kind: 'Variable', name: { kind: 'Name', value: 'orderedStageIds' } },
},
],
},
],
},
},
],
} as unknown as TypedDocumentNode<RaidStageReorderMutation, RaidStageReorderMutationVariables>

export interface RaidStageRemoveMutation {
readonly raidStageRemove: boolean
}

export interface RaidStageRemoveMutationVariables {
guildId: number
raidId: number
stageId: number
}

export const RaidStageRemoveDocument = {
kind: 'Document',
definitions: [
{
kind: 'OperationDefinition',
operation: 'mutation',
name: { kind: 'Name', value: 'RaidStageRemove' },
variableDefinitions: [
{
kind: 'VariableDefinition',
variable: { kind: 'Variable', name: { kind: 'Name', value: 'guildId' } },
type: {
kind: 'NonNullType',
type: { kind: 'NamedType', name: { kind: 'Name', value: 'Int' } },
},
},
{
kind: 'VariableDefinition',
variable: { kind: 'Variable', name: { kind: 'Name', value: 'raidId' } },
type: {
kind: 'NonNullType',
type: { kind: 'NamedType', name: { kind: 'Name', value: 'Int' } },
},
},
{
kind: 'VariableDefinition',
variable: { kind: 'Variable', name: { kind: 'Name', value: 'stageId' } },
type: {
kind: 'NonNullType',
type: { kind: 'NamedType', name: { kind: 'Name', value: 'Int' } },
},
},
],
selectionSet: {
kind: 'SelectionSet',
selections: [
{
kind: 'Field',
name: { kind: 'Name', value: 'raidStageRemove' },
arguments: [
{
kind: 'Argument',
name: { kind: 'Name', value: 'guildId' },
value: { kind: 'Variable', name: { kind: 'Name', value: 'guildId' } },
},
{
kind: 'Argument',
name: { kind: 'Name', value: 'raidId' },
value: { kind: 'Variable', name: { kind: 'Name', value: 'raidId' } },
},
{
kind: 'Argument',
name: { kind: 'Name', value: 'stageId' },
value: { kind: 'Variable', name: { kind: 'Name', value: 'stageId' } },
},
],
},
],
},
},
],
} as unknown as TypedDocumentNode<RaidStageRemoveMutation, RaidStageRemoveMutationVariables>

// ── Player tags (plan 05) ──────────────────────────────────────────────────

export interface PlayerTagsQuery {
playerTags: ReadonlyArray<{
readonly id: number
readonly guildId: number
readonly name: string
readonly seedKey: string | null
readonly color: string
readonly cursor: string
}>
}

export interface PlayerTagsQueryVariables {
guildId: number
}

export const PlayerTagsDocument = {
kind: 'Document',
definitions: [
{
kind: 'OperationDefinition',
operation: 'query',
name: { kind: 'Name', value: 'PlayerTags' },
variableDefinitions: [
{
kind: 'VariableDefinition',
variable: { kind: 'Variable', name: { kind: 'Name', value: 'guildId' } },
type: {
kind: 'NonNullType',
type: { kind: 'NamedType', name: { kind: 'Name', value: 'Int' } },
},
},
],
selectionSet: {
kind: 'SelectionSet',
selections: [
{
kind: 'Field',
name: { kind: 'Name', value: 'playerTags' },
arguments: [
{
kind: 'Argument',
name: { kind: 'Name', value: 'filters' },
value: {
  kind: 'ObjectValue',
  fields: [
    {
      kind: 'ObjectField',
      name: { kind: 'Name', value: 'guildId' },
      value: {
        kind: 'ObjectValue',
        fields: [
          {
            kind: 'ObjectField',
            name: { kind: 'Name', value: 'eq' },
            value: { kind: 'Variable', name: { kind: 'Name', value: 'guildId' } },
          },
        ],
      },
    },
  ],
},
},
{
kind: 'Argument',
name: { kind: 'Name', value: 'orderBy' },
value: {
  kind: 'ObjectValue',
  fields: [
    {
      kind: 'ObjectField',
      name: { kind: 'Name', value: 'name' },
      value: { kind: 'EnumValue', value: 'ASC' },
    },
  ],
},
},
],
selectionSet: {
kind: 'SelectionSet',
selections: [
{
kind: 'Field',
name: { kind: 'Name', value: 'edges' },
selectionSet: {
kind: 'SelectionSet',
selections: [
{
kind: 'Field',
name: { kind: 'Name', value: 'node' },
selectionSet: {
kind: 'SelectionSet',
selections: [
{ kind: 'Field', name: { kind: 'Name', value: 'id' } },
{ kind: 'Field', name: { kind: 'Name', value: 'guildId' } },
{ kind: 'Field', name: { kind: 'Name', value: 'name' } },
{ kind: 'Field', name: { kind: 'Name', value: 'seedKey' } },
{ kind: 'Field', name: { kind: 'Name', value: 'color' } },
],
},
},
{ kind: 'Field', name: { kind: 'Name', value: 'cursor' } },
],
},
},
],
},
},
],
},
},
],
} as unknown as TypedDocumentNode<PlayerTagsQuery, PlayerTagsQueryVariables>

export interface PlayerTagCreateMutation {
playerTagCreate: string
}

export interface PlayerTagCreateMutationVariables {
guildId: number
name: string
color?: string | null
}

export const PlayerTagCreateDocument = {
kind: 'Document',
definitions: [
{
kind: 'OperationDefinition',
operation: 'mutation',
name: { kind: 'Name', value: 'PlayerTagCreate' },
variableDefinitions: [
{
kind: 'VariableDefinition',
variable: { kind: 'Variable', name: { kind: 'Name', value: 'guildId' } },
type: {
kind: 'NonNullType',
type: { kind: 'NamedType', name: { kind: 'Name', value: 'Int' } },
},
},
{
kind: 'VariableDefinition',
variable: { kind: 'Variable', name: { kind: 'Name', value: 'name' } },
type: {
kind: 'NonNullType',
type: { kind: 'NamedType', name: { kind: 'Name', value: 'String' } },
},
},
{
kind: 'VariableDefinition',
variable: { kind: 'Variable', name: { kind: 'Name', value: 'color' } },
type: { kind: 'NamedType', name: { kind: 'Name', value: 'String' } },
},
],
selectionSet: {
kind: 'SelectionSet',
selections: [
{
kind: 'Field',
name: { kind: 'Name', value: 'playerTagCreate' },
arguments: [
{
kind: 'Argument',
name: { kind: 'Name', value: 'guildId' },
value: { kind: 'Variable', name: { kind: 'Name', value: 'guildId' } },
},
{
kind: 'Argument',
name: { kind: 'Name', value: 'name' },
value: { kind: 'Variable', name: { kind: 'Name', value: 'name' } },
},
{
kind: 'Argument',
name: { kind: 'Name', value: 'color' },
value: { kind: 'Variable', name: { kind: 'Name', value: 'color' } },
},
],
},
],
},
},
],
} as unknown as TypedDocumentNode<PlayerTagCreateMutation, PlayerTagCreateMutationVariables>

export interface PlayerTagRenameMutation {
playerTagRename: boolean
}

export interface PlayerTagRenameMutationVariables {
guildId: number
tagId: number
name: string
}

export const PlayerTagRenameDocument = {
kind: 'Document',
definitions: [
{
kind: 'OperationDefinition',
operation: 'mutation',
name: { kind: 'Name', value: 'PlayerTagRename' },
variableDefinitions: [
{
kind: 'VariableDefinition',
variable: { kind: 'Variable', name: { kind: 'Name', value: 'guildId' } },
type: {
kind: 'NonNullType',
type: { kind: 'NamedType', name: { kind: 'Name', value: 'Int' } },
},
},
{
kind: 'VariableDefinition',
variable: { kind: 'Variable', name: { kind: 'Name', value: 'tagId' } },
type: {
kind: 'NonNullType',
type: { kind: 'NamedType', name: { kind: 'Name', value: 'Int' } },
},
},
{
kind: 'VariableDefinition',
variable: { kind: 'Variable', name: { kind: 'Name', value: 'name' } },
type: {
kind: 'NonNullType',
type: { kind: 'NamedType', name: { kind: 'Name', value: 'String' } },
},
},
],
selectionSet: {
kind: 'SelectionSet',
selections: [
{
kind: 'Field',
name: { kind: 'Name', value: 'playerTagRename' },
arguments: [
{
kind: 'Argument',
name: { kind: 'Name', value: 'guildId' },
value: { kind: 'Variable', name: { kind: 'Name', value: 'guildId' } },
},
{
kind: 'Argument',
name: { kind: 'Name', value: 'tagId' },
value: { kind: 'Variable', name: { kind: 'Name', value: 'tagId' } },
},
{
kind: 'Argument',
name: { kind: 'Name', value: 'name' },
value: { kind: 'Variable', name: { kind: 'Name', value: 'name' } },
},
],
},
],
},
},
],
} as unknown as TypedDocumentNode<PlayerTagRenameMutation, PlayerTagRenameMutationVariables>

export interface PlayerTagSetColorMutation {
playerTagSetColor: boolean
}

export interface PlayerTagSetColorMutationVariables {
guildId: number
tagId: number
color: string
}

export const PlayerTagSetColorDocument = {
kind: 'Document',
definitions: [
{
kind: 'OperationDefinition',
operation: 'mutation',
name: { kind: 'Name', value: 'PlayerTagSetColor' },
variableDefinitions: [
{
kind: 'VariableDefinition',
variable: { kind: 'Variable', name: { kind: 'Name', value: 'guildId' } },
type: {
kind: 'NonNullType',
type: { kind: 'NamedType', name: { kind: 'Name', value: 'Int' } },
},
},
{
kind: 'VariableDefinition',
variable: { kind: 'Variable', name: { kind: 'Name', value: 'tagId' } },
type: {
kind: 'NonNullType',
type: { kind: 'NamedType', name: { kind: 'Name', value: 'Int' } },
},
},
{
kind: 'VariableDefinition',
variable: { kind: 'Variable', name: { kind: 'Name', value: 'color' } },
type: {
kind: 'NonNullType',
type: { kind: 'NamedType', name: { kind: 'Name', value: 'String' } },
},
},
],
selectionSet: {
kind: 'SelectionSet',
selections: [
{
kind: 'Field',
name: { kind: 'Name', value: 'playerTagSetColor' },
arguments: [
{
kind: 'Argument',
name: { kind: 'Name', value: 'guildId' },
value: { kind: 'Variable', name: { kind: 'Name', value: 'guildId' } },
},
{
kind: 'Argument',
name: { kind: 'Name', value: 'tagId' },
value: { kind: 'Variable', name: { kind: 'Name', value: 'tagId' } },
},
{
kind: 'Argument',
name: { kind: 'Name', value: 'color' },
value: { kind: 'Variable', name: { kind: 'Name', value: 'color' } },
},
],
},
],
},
},
],
} as unknown as TypedDocumentNode<PlayerTagSetColorMutation, PlayerTagSetColorMutationVariables>

export interface PlayerTagDeleteMutation {
playerTagDelete: boolean
}

export interface PlayerTagDeleteMutationVariables {
guildId: number
tagId: number
}

export const PlayerTagDeleteDocument = {
kind: 'Document',
definitions: [
{
kind: 'OperationDefinition',
operation: 'mutation',
name: { kind: 'Name', value: 'PlayerTagDelete' },
variableDefinitions: [
{
kind: 'VariableDefinition',
variable: { kind: 'Variable', name: { kind: 'Name', value: 'guildId' } },
type: {
kind: 'NonNullType',
type: { kind: 'NamedType', name: { kind: 'Name', value: 'Int' } },
},
},
{
kind: 'VariableDefinition',
variable: { kind: 'Variable', name: { kind: 'Name', value: 'tagId' } },
type: {
kind: 'NonNullType',
type: { kind: 'NamedType', name: { kind: 'Name', value: 'Int' } },
},
},
],
selectionSet: {
kind: 'SelectionSet',
selections: [
{
kind: 'Field',
name: { kind: 'Name', value: 'playerTagDelete' },
arguments: [
{
kind: 'Argument',
name: { kind: 'Name', value: 'guildId' },
value: { kind: 'Variable', name: { kind: 'Name', value: 'guildId' } },
},
{
kind: 'Argument',
name: { kind: 'Name', value: 'tagId' },
value: { kind: 'Variable', name: { kind: 'Name', value: 'tagId' } },
},
],
},
],
},
},
],
} as unknown as TypedDocumentNode<PlayerTagDeleteMutation, PlayerTagDeleteMutationVariables>

export interface PlayerTagAssignMutation {
playerTagAssign: boolean
}

export interface PlayerTagAssignMutationVariables {
guildId: number
playerId: number
tagId: number
}

export const PlayerTagAssignDocument = {
kind: 'Document',
definitions: [
{
kind: 'OperationDefinition',
operation: 'mutation',
name: { kind: 'Name', value: 'PlayerTagAssign' },
variableDefinitions: [
{
kind: 'VariableDefinition',
variable: { kind: 'Variable', name: { kind: 'Name', value: 'guildId' } },
type: {
kind: 'NonNullType',
type: { kind: 'NamedType', name: { kind: 'Name', value: 'Int' } },
},
},
{
kind: 'VariableDefinition',
variable: { kind: 'Variable', name: { kind: 'Name', value: 'playerId' } },
type: {
kind: 'NonNullType',
type: { kind: 'NamedType', name: { kind: 'Name', value: 'Int' } },
},
},
{
kind: 'VariableDefinition',
variable: { kind: 'Variable', name: { kind: 'Name', value: 'tagId' } },
type: {
kind: 'NonNullType',
type: { kind: 'NamedType', name: { kind: 'Name', value: 'Int' } },
},
},
],
selectionSet: {
kind: 'SelectionSet',
selections: [
{
kind: 'Field',
name: { kind: 'Name', value: 'playerTagAssign' },
arguments: [
{
kind: 'Argument',
name: { kind: 'Name', value: 'guildId' },
value: { kind: 'Variable', name: { kind: 'Name', value: 'guildId' } },
},
{
kind: 'Argument',
name: { kind: 'Name', value: 'playerId' },
value: { kind: 'Variable', name: { kind: 'Name', value: 'playerId' } },
},
{
kind: 'Argument',
name: { kind: 'Name', value: 'tagId' },
value: { kind: 'Variable', name: { kind: 'Name', value: 'tagId' } },
},
],
},
],
},
},
],
} as unknown as TypedDocumentNode<PlayerTagAssignMutation, PlayerTagAssignMutationVariables>

export interface PlayerTagUnassignMutation {
playerTagUnassign: boolean
}

export interface PlayerTagUnassignMutationVariables {
guildId: number
playerId: number
tagId: number
}

export const PlayerTagUnassignDocument = {
kind: 'Document',
definitions: [
{
kind: 'OperationDefinition',
operation: 'mutation',
name: { kind: 'Name', value: 'PlayerTagUnassign' },
variableDefinitions: [
{
kind: 'VariableDefinition',
variable: { kind: 'Variable', name: { kind: 'Name', value: 'guildId' } },
type: {
kind: 'NonNullType',
type: { kind: 'NamedType', name: { kind: 'Name', value: 'Int' } },
},
},
{
kind: 'VariableDefinition',
variable: { kind: 'Variable', name: { kind: 'Name', value: 'playerId' } },
type: {
kind: 'NonNullType',
type: { kind: 'NamedType', name: { kind: 'Name', value: 'Int' } },
},
},
{
kind: 'VariableDefinition',
variable: { kind: 'Variable', name: { kind: 'Name', value: 'tagId' } },
type: {
kind: 'NonNullType',
type: { kind: 'NamedType', name: { kind: 'Name', value: 'Int' } },
},
},
],
selectionSet: {
kind: 'SelectionSet',
selections: [
{
kind: 'Field',
name: { kind: 'Name', value: 'playerTagUnassign' },
arguments: [
{
kind: 'Argument',
name: { kind: 'Name', value: 'guildId' },
value: { kind: 'Variable', name: { kind: 'Name', value: 'guildId' } },
},
{
kind: 'Argument',
name: { kind: 'Name', value: 'playerId' },
value: { kind: 'Variable', name: { kind: 'Name', value: 'playerId' } },
},
{
kind: 'Argument',
name: { kind: 'Name', value: 'tagId' },
value: { kind: 'Variable', name: { kind: 'Name', value: 'tagId' } },
},
],
},
],
},
},
],
} as unknown as TypedDocumentNode<PlayerTagUnassignMutation, PlayerTagUnassignMutationVariables>

export interface RaidPlayerTagAssignMutation {
  readonly raidPlayerTagAssign: boolean
}

export interface RaidPlayerTagAssignMutationVariables {
  readonly guildId: string | number
  readonly raidId: number
  readonly raidPlayerId: number
  readonly tagId: number
  readonly stageId: number | null
}

export const RaidPlayerTagAssignDocument = parse(`
  mutation RaidPlayerTagAssign(
    $guildId: Int!
    $raidId: Int!
    $raidPlayerId: Int!
    $tagId: Int!
    $stageId: Int
  ) {
    raidPlayerTagAssign(
      guildId: $guildId
      raidId: $raidId
      raidPlayerId: $raidPlayerId
      tagId: $tagId
      stageId: $stageId
    )
  }
`) as TypedDocumentNode<RaidPlayerTagAssignMutation, RaidPlayerTagAssignMutationVariables>

export interface RaidPlayerTagUnassignMutation {
  readonly raidPlayerTagUnassign: boolean
}

export interface RaidPlayerTagUnassignMutationVariables {
  readonly guildId: string | number
  readonly raidId: number
  readonly raidPlayerId: number
  readonly tagId: number
  readonly stageId: number | null
}

export const RaidPlayerTagUnassignDocument = parse(`
  mutation RaidPlayerTagUnassign(
    $guildId: Int!
    $raidId: Int!
    $raidPlayerId: Int!
    $tagId: Int!
    $stageId: Int
  ) {
    raidPlayerTagUnassign(
      guildId: $guildId
      raidId: $raidId
      raidPlayerId: $raidPlayerId
      tagId: $tagId
      stageId: $stageId
    )
  }
`) as TypedDocumentNode<RaidPlayerTagUnassignMutation, RaidPlayerTagUnassignMutationVariables>

export interface RaidStageDiffsResetMutation {
  readonly raidStageDiffsReset: boolean
}

export interface RaidStageDiffsResetMutationVariables {
  readonly guildId: string | number
  readonly raidId: number
  readonly stageId: number
}

export const RaidStageDiffsResetDocument = parse(`
  mutation RaidStageDiffsReset($guildId: Int!, $raidId: Int!, $stageId: Int!) {
    raidStageDiffsReset(guildId: $guildId, raidId: $raidId, stageId: $stageId)
  }
`) as TypedDocumentNode<RaidStageDiffsResetMutation, RaidStageDiffsResetMutationVariables>

export interface RaidAllStageDiffsResetMutation {
  readonly raidAllStageDiffsReset: boolean
}

export interface RaidAllStageDiffsResetMutationVariables {
  readonly guildId: string | number
  readonly raidId: number
}

export const RaidAllStageDiffsResetDocument = parse(`
  mutation RaidAllStageDiffsReset($guildId: Int!, $raidId: Int!) {
    raidAllStageDiffsReset(guildId: $guildId, raidId: $raidId)
  }
`) as TypedDocumentNode<RaidAllStageDiffsResetMutation, RaidAllStageDiffsResetMutationVariables>

export interface PlayerTagResetAllMutation {
playerTagResetAll: boolean
}

export interface PlayerTagResetAllMutationVariables {
guildId: number
}

export const PlayerTagResetAllDocument = {
kind: 'Document',
definitions: [
{
kind: 'OperationDefinition',
operation: 'mutation',
name: { kind: 'Name', value: 'PlayerTagResetAll' },
variableDefinitions: [
{
kind: 'VariableDefinition',
variable: { kind: 'Variable', name: { kind: 'Name', value: 'guildId' } },
type: {
kind: 'NonNullType',
type: { kind: 'NamedType', name: { kind: 'Name', value: 'Int' } },
},
},
],
selectionSet: {
kind: 'SelectionSet',
selections: [
{
kind: 'Field',
name: { kind: 'Name', value: 'playerTagResetAll' },
arguments: [
{
kind: 'Argument',
name: { kind: 'Name', value: 'guildId' },
value: { kind: 'Variable', name: { kind: 'Name', value: 'guildId' } },
},
],
},
],
},
},
],
} as unknown as TypedDocumentNode<PlayerTagResetAllMutation, PlayerTagResetAllMutationVariables>

// ── My player builds (cross-server, plan my-builds) ────────────────────────

/**
 * Hand-written `myPlayerBuilds(discordUserId: Int!)` query.
 *
 * Pulls a player's saved cross-server build library directly by Discord id,
 * bypassing the active guild's board. Lives next to the other hand-written
 * documents because the codegen snapshot in `apollo/gql/graphql.ts` doesn't
 * carry the new `myPlayerBuilds` field yet (it was added for the
 * /my-builds route alongside the discord-id-keyed build editor — see
 * `apps/backend/api/src/board.rs`).
 *
 * The server returns `[[String!]!]!` — each inner array is one saved
 * build's weapon slugs, ordered by `order_index` ASC. The query selects
 * the flat list-shape so the consumer (`useMyBuilds`) can project each
 * row straight into its `ReadonlyArray<ReadonlyArray<string>>` store
 * value without unwrapping a per-row object.
 */
export interface MyPlayerBuildsQuery {
  myPlayerBuilds: ReadonlyArray<ReadonlyArray<string>>
}

export interface MyPlayerBuildsQueryVariables {
  // discordUserId is typed string | number so callers can pass the Discord
  // snowflake as a string — the Apollo snowflake rewriter emits a bare JSON
  // number on the wire (see src/apollo/snowflakes.ts), so async-graphql's
  // i64 parser sees the exact 17-digit value without Number() truncation.
  discordUserId: string | number
}

export const MyPlayerBuildsDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'MyPlayerBuilds' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'discordUserId' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'Int' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'myPlayerBuilds' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'discordUserId' },
                value: {
                  kind: 'Variable',
                  name: { kind: 'Name', value: 'discordUserId' },
                },
              },
            ],
          },
        ],
      },
    },
  ],
} as unknown as TypedDocumentNode<MyPlayerBuildsQuery, MyPlayerBuildsQueryVariables>

/**
 * Hand-written `myAddBuild(discordUserId: Int!, weapons: [String!]!): ID!`
 * mutation.
 *
 * Appends a new saved build to the player's cross-server library and
 * returns the new row's id. Self-only — the resolver does not require
 * the player to be on any particular guild's board. Pairs with
 * `MyPlayerBuildsDocument` (read) / `MySetBuildDocument` (replace) /
 * `MyRemoveBuildDocument` (delete) / `MySetActiveBuildDocument`
 * (mark active) to back the `/my-builds` route's cross-server build
 * editor.
 */
export interface MyAddBuildMutation {
  myAddBuild: string
}

export interface MyAddBuildMutationVariables {
  discordUserId: string | number
  weapons: ReadonlyArray<string>
}

export const MyAddBuildDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'MyAddBuild' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'discordUserId' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'Int' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'weapons' } },
          type: {
            kind: 'NonNullType',
            type: {
              kind: 'ListType',
              type: {
                kind: 'NonNullType',
                type: { kind: 'NamedType', name: { kind: 'Name', value: 'String' } },
              },
            },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'myAddBuild' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'discordUserId' },
                value: {
                  kind: 'Variable',
                  name: { kind: 'Name', value: 'discordUserId' },
                },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'weapons' },
                value: {
                  kind: 'Variable',
                  name: { kind: 'Name', value: 'weapons' },
                },
              },
            ],
          },
        ],
      },
    },
  ],
} as unknown as TypedDocumentNode<MyAddBuildMutation, MyAddBuildMutationVariables>

/**
 * Hand-written `mySetBuild(discordUserId: Int!, index: Int!, weapons: [String!]!): Boolean!`
 * mutation.
 *
 * Replaces the saved build at the given `index` in the player's
 * cross-server library with the supplied weapon list. Returns `true`
 * on success. Self-only — mirrors the live path's `playerSetBuild`
 * contract but keys off the Discord id.
 */
export interface MySetBuildMutation {
  mySetBuild: boolean
}

export interface MySetBuildMutationVariables {
  discordUserId: string | number
  index: number
  weapons: ReadonlyArray<string>
}

export const MySetBuildDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'MySetBuild' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'discordUserId' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'Int' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'index' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'Int' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'weapons' } },
          type: {
            kind: 'NonNullType',
            type: {
              kind: 'ListType',
              type: {
                kind: 'NonNullType',
                type: { kind: 'NamedType', name: { kind: 'Name', value: 'String' } },
              },
            },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'mySetBuild' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'discordUserId' },
                value: {
                  kind: 'Variable',
                  name: { kind: 'Name', value: 'discordUserId' },
                },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'index' },
                value: {
                  kind: 'Variable',
                  name: { kind: 'Name', value: 'index' },
                },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'weapons' },
                value: {
                  kind: 'Variable',
                  name: { kind: 'Name', value: 'weapons' },
                },
              },
            ],
          },
        ],
      },
    },
  ],
} as unknown as TypedDocumentNode<MySetBuildMutation, MySetBuildMutationVariables>

/**
 * Hand-written `myRemoveBuild(discordUserId: Int!, index: Int!): Boolean!`
 * mutation.
 *
 * Deletes the saved build at the given `index` from the player's
 * cross-server library. Returns `true` on success. Self-only —
 * remaining rows above the removed index keep their original
 * `order_index`, so re-fetching the library through
 * `MyPlayerBuildsDocument` after this write keeps the UI in sync.
 */
export interface MyRemoveBuildMutation {
  myRemoveBuild: boolean
}

export interface MyRemoveBuildMutationVariables {
  discordUserId: string | number
  index: number
}

export const MyRemoveBuildDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'MyRemoveBuild' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'discordUserId' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'Int' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'index' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'Int' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'myRemoveBuild' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'discordUserId' },
                value: {
                  kind: 'Variable',
                  name: { kind: 'Name', value: 'discordUserId' },
                },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'index' },
                value: {
                  kind: 'Variable',
                  name: { kind: 'Name', value: 'index' },
                },
              },
            ],
          },
        ],
      },
    },
  ],
} as unknown as TypedDocumentNode<MyRemoveBuildMutation, MyRemoveBuildMutationVariables>

/**
 * Hand-written `mySetActiveBuild(discordUserId: Int!, index: Int!): Boolean!`
 * mutation.
 *
 * Marks the saved build at the given `index` as the active build for
 * the player's cross-server library. Pass `-1` to clear the active
 * build and fall back to `mainWeapons`. Self-only — same Discord-id
 * key as the rest of the `my*Build*` family.
 */
export interface MySetActiveBuildMutation {
  mySetActiveBuild: boolean
}

export interface MySetActiveBuildMutationVariables {
  discordUserId: string | number
  index: number
}

export const MySetActiveBuildDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'MySetActiveBuild' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'discordUserId' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'Int' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'index' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'Int' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'mySetActiveBuild' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'discordUserId' },
                value: {
                  kind: 'Variable',
                  name: { kind: 'Name', value: 'discordUserId' },
                },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'index' },
                value: {
                  kind: 'Variable',
                  name: { kind: 'Name', value: 'index' },
                },
              },
            ],
          },
        ],
      },
    },
  ],
} as unknown as TypedDocumentNode<MySetActiveBuildMutation, MySetActiveBuildMutationVariables>

export interface MySetDefaultBuildMutation {
  readonly mySetDefaultBuild: ReadonlyArray<ReadonlyArray<string>>
}

export interface MySetDefaultBuildMutationVariables {
  readonly discordUserId: string | number
  readonly index: number
}

/**
 * Move a personal build to slot 1, which is the cross-server default used for
 * raid-roster fallback. The mutation returns the authoritative ordered library
 * so the standalone manager never has to refetch after this write.
 */
export const MySetDefaultBuildDocument = parse(`
  mutation MySetDefaultBuild($discordUserId: Int!, $index: Int!) {
    mySetDefaultBuild(discordUserId: $discordUserId, index: $index)
  }
`) as unknown as TypedDocumentNode<
  MySetDefaultBuildMutation,
  MySetDefaultBuildMutationVariables
>

// ── Raid tactical map + forest rotations ────────────────────────────────

interface RaidMapGroupPositionWire {
  readonly raidGroupId: string | number
  readonly xBasisPoints: number
  readonly yBasisPoints: number
}

interface RaidMapTreePositionWire {
  readonly xBasisPoints: number
  readonly yBasisPoints: number
}

interface RaidForestAssignmentWire {
  readonly id: string | number
  readonly raidPlayerId: string | number
  readonly locationKey: string
  readonly spawnOffsetMinutes: number
}

export interface RaidMapPlanQuery {
  readonly raidMapPlan: {
    readonly groupPositions: ReadonlyArray<RaidMapGroupPositionWire>
    readonly treePosition: RaidMapTreePositionWire | null
    readonly forestAssignments: ReadonlyArray<RaidForestAssignmentWire>
  }
}

export interface RaidMapPlanQueryVariables {
  readonly guildId: string
  readonly raidId: string
}

export const RaidMapPlanDocument = parse(`
  query RaidMapPlan($guildId: Int!, $raidId: ID!) {
    raidMapPlan(guildId: $guildId, raidId: $raidId) {
      groupPositions {
        raidGroupId
        xBasisPoints
        yBasisPoints
      }
      treePosition {
        xBasisPoints
        yBasisPoints
      }
      forestAssignments {
        id
        raidPlayerId
        locationKey
        spawnOffsetMinutes
      }
    }
  }
`) as TypedDocumentNode<RaidMapPlanQuery, RaidMapPlanQueryVariables>

export interface RaidMapGroupPositionSetMutation {
  readonly raidMapGroupPositionSet: boolean
}

export interface RaidMapGroupPositionSetMutationVariables {
  readonly guildId: string
  readonly raidId: string
  readonly raidGroupId: string
  readonly xBasisPoints: number
  readonly yBasisPoints: number
}

export const RaidMapGroupPositionSetDocument = parse(`
  mutation RaidMapGroupPositionSet(
    $guildId: Int!
    $raidId: ID!
    $raidGroupId: ID!
    $xBasisPoints: Int!
    $yBasisPoints: Int!
  ) {
    raidMapGroupPositionSet(
      guildId: $guildId
      raidId: $raidId
      raidGroupId: $raidGroupId
      xBasisPoints: $xBasisPoints
      yBasisPoints: $yBasisPoints
    )
  }
`) as TypedDocumentNode<
  RaidMapGroupPositionSetMutation,
  RaidMapGroupPositionSetMutationVariables
>

export interface RaidMapTreePositionSetMutation {
  readonly raidMapTreePositionSet: boolean
}

export interface RaidMapTreePositionSetMutationVariables {
  readonly guildId: string
  readonly raidId: string
  readonly xBasisPoints: number
  readonly yBasisPoints: number
}

export const RaidMapTreePositionSetDocument = parse(`
  mutation RaidMapTreePositionSet(
    $guildId: Int!
    $raidId: ID!
    $xBasisPoints: Int!
    $yBasisPoints: Int!
  ) {
    raidMapTreePositionSet(
      guildId: $guildId
      raidId: $raidId
      xBasisPoints: $xBasisPoints
      yBasisPoints: $yBasisPoints
    )
  }
`) as TypedDocumentNode<
  RaidMapTreePositionSetMutation,
  RaidMapTreePositionSetMutationVariables
>

export interface RaidForestAssignmentUpsertMutation {
  readonly raidForestAssignmentUpsert: boolean
}

export interface RaidForestAssignmentUpsertMutationVariables {
  readonly guildId: string
  readonly raidId: string
  readonly raidPlayerId: string
  readonly locationKey: string
  readonly spawnOffsetMinutes: number
}

export const RaidForestAssignmentUpsertDocument = parse(`
  mutation RaidForestAssignmentUpsert(
    $guildId: Int!
    $raidId: ID!
    $raidPlayerId: ID!
    $locationKey: String!
    $spawnOffsetMinutes: Int!
  ) {
    raidForestAssignmentUpsert(
      guildId: $guildId
      raidId: $raidId
      raidPlayerId: $raidPlayerId
      locationKey: $locationKey
      spawnOffsetMinutes: $spawnOffsetMinutes
    )
  }
`) as TypedDocumentNode<
  RaidForestAssignmentUpsertMutation,
  RaidForestAssignmentUpsertMutationVariables
>

export interface RaidForestAssignmentRemoveMutation {
  readonly raidForestAssignmentRemove: boolean
}

export interface RaidForestAssignmentRemoveMutationVariables {
  readonly guildId: string
  readonly raidId: string
  readonly assignmentId: string
}

export const RaidForestAssignmentRemoveDocument = parse(`
  mutation RaidForestAssignmentRemove(
    $guildId: Int!
    $raidId: ID!
    $assignmentId: ID!
  ) {
    raidForestAssignmentRemove(
      guildId: $guildId
      raidId: $raidId
      assignmentId: $assignmentId
    )
  }
`) as TypedDocumentNode<
  RaidForestAssignmentRemoveMutation,
  RaidForestAssignmentRemoveMutationVariables
>

export interface GuildNotificationCapabilitiesWire {
  readonly guildId: string
  readonly coordinatorPresent: boolean
  readonly bothBotsPresent: boolean
  readonly configuredBotAccounts: number
  readonly availableBotAccounts: number
  readonly maxConcurrentChannels: number
  readonly teamspeakConfigured: boolean
  readonly botAccountStatuses: ReadonlyArray<GuildNotificationBotAccountStatusWire>
  readonly supportedModes: ReadonlyArray<string>
}

export interface GuildNotificationBotAccountStatusWire {
  readonly index: number
  readonly role: string
  readonly ready: boolean
  readonly guildPresent: boolean
  readonly inviteUrl: string | null
  readonly requiresOauthCodeGrant: boolean
}

export interface GuildNotificationSettingsWire {
  readonly guildId: string
  readonly enabled: boolean
  readonly automatic: boolean
  readonly intervalSeconds: number
  readonly notificationOffsetsSeconds: ReadonlyArray<number>
  readonly mode: string
  readonly voiceChannelIds: ReadonlyArray<string>
  readonly teamspeakChannelIds: ReadonlyArray<string>
  readonly soundboardSoundId: string | null
  readonly soundboardSourceGuildId: string | null
}

export interface GuildDiscordReferenceWire {
  readonly kind: string
  readonly id: string
  readonly name: string | null
  readonly issue: string | null
  readonly foreignGuildBotAccountStatuses: ReadonlyArray<GuildDiscordReferenceBotAccountStatusWire>
}

export interface GuildDiscordReferenceBotAccountStatusWire {
  readonly index: number
  readonly role: string
  readonly inviteUrl: string | null
  readonly requiresOauthCodeGrant: boolean
}

export interface GuildNotificationCapabilitiesQuery {
  readonly guildNotificationCapabilities: GuildNotificationCapabilitiesWire | null
}

export interface GuildNotificationSettingsQuery {
  readonly guildNotificationSettings: GuildNotificationSettingsWire | null
}

export interface GuildNotificationTeamspeakChannelWire {
  readonly id: string
  readonly name: string
  readonly parentId: string | null
}

export interface GuildNotificationTeamspeakChannelsQuery {
  readonly guildNotificationTeamspeakChannels: {
    readonly guildId: string
    readonly channels: ReadonlyArray<GuildNotificationTeamspeakChannelWire>
  }
}

export const GuildNotificationTeamspeakChannelsDocument = parse(`
  query GuildNotificationTeamspeakChannels($guildId: String!) {
    guildNotificationTeamspeakChannels(guildId: $guildId) {
      guildId
      channels { id name parentId }
    }
  }
`) as unknown as TypedDocumentNode<
  GuildNotificationTeamspeakChannelsQuery,
  { readonly guildId: string }
>

export interface GuildDiscordReferencesQuery {
  readonly guildDiscordReferences: ReadonlyArray<GuildDiscordReferenceWire>
}

export interface GuildNotificationQueryVariables {
  readonly guildId: string
}

export interface GuildDiscordReferencesQueryVariables
  extends GuildNotificationQueryVariables {
  readonly roleIds: ReadonlyArray<string>
  readonly channelIds: ReadonlyArray<string>
}

export interface SetGuildNotificationSettingsMutation {
  readonly setGuildNotificationSettings: GuildNotificationSettingsWire
}

export interface TestGuildNotificationSettingsMutation {
  readonly testGuildNotificationSettings: string
}

export interface SetGuildNotificationSettingsMutationVariables
  extends GuildNotificationQueryVariables {
  readonly enabled: boolean
  readonly automatic: boolean
  readonly intervalSeconds: number
  readonly notificationOffsetsSeconds: ReadonlyArray<number>
  readonly mode: string
  readonly voiceChannelIds: ReadonlyArray<string>
  readonly teamspeakChannelIds: ReadonlyArray<string>
  readonly soundboardSoundId: string | null
  readonly soundboardSourceGuildId: string | null
}

export interface TestGuildNotificationSettingsMutationVariables
  extends SetGuildNotificationSettingsMutationVariables {}

export const GuildNotificationCapabilitiesDocument = parse(`
  query GuildNotificationCapabilities($guildId: String!) {
    guildNotificationCapabilities(guildId: $guildId) {
      guildId
      coordinatorPresent
      bothBotsPresent
      configuredBotAccounts
      availableBotAccounts
      maxConcurrentChannels
      teamspeakConfigured
      botAccountStatuses {
        index
        role
        ready
        guildPresent
        inviteUrl
        requiresOauthCodeGrant
      }
      supportedModes
    }
  }
`) as TypedDocumentNode<
  GuildNotificationCapabilitiesQuery,
  GuildNotificationQueryVariables
>

export const GuildNotificationSettingsDocument = parse(`
  query GuildNotificationSettings($guildId: String!) {
    guildNotificationSettings(guildId: $guildId) {
      guildId
      enabled
      automatic
      intervalSeconds
      notificationOffsetsSeconds
      mode
      voiceChannelIds
      teamspeakChannelIds
      soundboardSoundId
      soundboardSourceGuildId
    }
  }
`) as TypedDocumentNode<
  GuildNotificationSettingsQuery,
  GuildNotificationQueryVariables
>

export const GuildDiscordReferencesDocument = parse(`
  query GuildDiscordReferences(
    $guildId: String!
    $roleIds: [String!]!
    $channelIds: [String!]!
  ) {
    guildDiscordReferences(
      guildId: $guildId
      roleIds: $roleIds
      channelIds: $channelIds
    ) {
      kind
      id
      name
      issue
      foreignGuildBotAccountStatuses {
        index
        role
        inviteUrl
        requiresOauthCodeGrant
      }
    }
  }
`) as TypedDocumentNode<
  GuildDiscordReferencesQuery,
  GuildDiscordReferencesQueryVariables
>

export const SetGuildNotificationSettingsDocument = parse(`
  mutation SetGuildNotificationSettings(
    $guildId: String!
    $enabled: Boolean!
    $automatic: Boolean!
    $intervalSeconds: Int!
    $notificationOffsetsSeconds: [Int!]!
    $mode: String!
    $voiceChannelIds: [String!]!
    $teamspeakChannelIds: [String!]!
    $soundboardSoundId: String
    $soundboardSourceGuildId: String
  ) {
    setGuildNotificationSettings(
      guildId: $guildId
      enabled: $enabled
      automatic: $automatic
      intervalSeconds: $intervalSeconds
      notificationOffsetsSeconds: $notificationOffsetsSeconds
      mode: $mode
      voiceChannelIds: $voiceChannelIds
      teamspeakChannelIds: $teamspeakChannelIds
      soundboardSoundId: $soundboardSoundId
      soundboardSourceGuildId: $soundboardSourceGuildId
    ) {
      guildId
      enabled
      automatic
      intervalSeconds
      notificationOffsetsSeconds
      mode
      voiceChannelIds
      teamspeakChannelIds
      soundboardSoundId
      soundboardSourceGuildId
    }
  }
`) as TypedDocumentNode<
  SetGuildNotificationSettingsMutation,
  SetGuildNotificationSettingsMutationVariables
>

export const TestGuildNotificationSettingsDocument = parse(`
  mutation TestGuildNotificationSettings(
    $guildId: String!
    $enabled: Boolean!
    $automatic: Boolean!
    $intervalSeconds: Int!
    $notificationOffsetsSeconds: [Int!]!
    $mode: String!
    $voiceChannelIds: [String!]!
    $teamspeakChannelIds: [String!]!
    $soundboardSoundId: String
    $soundboardSourceGuildId: String
  ) {
    testGuildNotificationSettings(
      guildId: $guildId
      enabled: $enabled
      automatic: $automatic
      intervalSeconds: $intervalSeconds
      notificationOffsetsSeconds: $notificationOffsetsSeconds
      mode: $mode
      voiceChannelIds: $voiceChannelIds
      teamspeakChannelIds: $teamspeakChannelIds
      soundboardSoundId: $soundboardSoundId
      soundboardSourceGuildId: $soundboardSourceGuildId
    )
  }
`) as TypedDocumentNode<
  TestGuildNotificationSettingsMutation,
  TestGuildNotificationSettingsMutationVariables
>

export type GuildNotificationTimerSourceWire = 'manual' | 'external'

export type GuildNotificationTimerStatusWire =
  | 'scheduled'
  | 'running'
  | 'finished'
  | 'stopped'

export interface GuildNotificationTimerWire {
  readonly id: string
  readonly name: string
  readonly source: GuildNotificationTimerSourceWire
  readonly startsAt: string
  readonly endsAt: string
  readonly durationSeconds: number
  readonly active: boolean
  readonly cancelledAt: string | null
  readonly status: GuildNotificationTimerStatusWire
  readonly canModify: boolean
}

export interface GuildNotificationTimerListWire {
  readonly guildId: string
  readonly timers: ReadonlyArray<GuildNotificationTimerWire>
}

export interface GuildNotificationTimerStatusChannelWire {
  readonly guildId: string
  readonly channelId: string | null
  readonly messageId: string | null
}

export interface GuildNotificationTimersQuery {
  readonly guildNotificationTimers: GuildNotificationTimerListWire
}

export interface GuildNotificationTimerStatusChannelQuery {
  readonly guildNotificationTimerStatusChannel: GuildNotificationTimerStatusChannelWire
}

export interface AddGuildNotificationTimerMutation {
  readonly addGuildNotificationTimer: GuildNotificationTimerWire
}

export interface AddGuildNotificationTimerMutationVariables {
  readonly guildId: string
  readonly name: string | null
  readonly gameTimer: string | null
}

export interface ModifyGuildNotificationTimerMutation {
  readonly modifyGuildNotificationTimer: GuildNotificationTimerWire
}

export interface ModifyGuildNotificationTimerMutationVariables {
  readonly guildId: string
  readonly timerId: string
  readonly name: string | null
  readonly gameTimer: string | null
}

export interface StartGuildNotificationTimerMutation {
  readonly startGuildNotificationTimer: GuildNotificationTimerWire
}

export interface StartGuildNotificationTimerMutationVariables {
  readonly guildId: string
  readonly timerId: string
  readonly gameTimer: string
}

export interface StopGuildNotificationTimerMutation {
  readonly stopGuildNotificationTimer: GuildNotificationTimerWire
}

export interface StopGuildNotificationTimerMutationVariables {
  readonly guildId: string
  readonly timerId: string
}

export interface GuildNotificationTimerRemovalWire {
  readonly guildId: string
  readonly timerId: string
  readonly removed: boolean
}

export interface RemoveGuildNotificationTimerMutation {
  readonly removeGuildNotificationTimer: GuildNotificationTimerRemovalWire
}

export interface RemoveGuildNotificationTimerMutationVariables {
  readonly guildId: string
  readonly timerId: string
}

export interface SetGuildNotificationTimerStatusChannelMutation {
  readonly setGuildNotificationTimerStatusChannel: GuildNotificationTimerStatusChannelWire
}

export interface SetGuildNotificationTimerStatusChannelMutationVariables {
  readonly guildId: string
  readonly channelId: string | null
}

export const GuildNotificationTimersDocument = parse(`
  query GuildNotificationTimers($guildId: String!) {
    guildNotificationTimers(guildId: $guildId) {
      guildId
      timers {
        id
        name
        source
        startsAt
        endsAt
        durationSeconds
        active
        cancelledAt
        status
        canModify
      }
    }
  }
`) as TypedDocumentNode<
  GuildNotificationTimersQuery,
  GuildNotificationQueryVariables
>

export const GuildNotificationTimerStatusChannelDocument = parse(`
  query GuildNotificationTimerStatusChannel($guildId: String!) {
    guildNotificationTimerStatusChannel(guildId: $guildId) {
      guildId
      channelId
      messageId
    }
  }
`) as TypedDocumentNode<
  GuildNotificationTimerStatusChannelQuery,
  GuildNotificationQueryVariables
>

export const AddGuildNotificationTimerDocument = parse(`
  mutation AddGuildNotificationTimer(
    $guildId: String!
    $name: String
    $gameTimer: String
  ) {
    addGuildNotificationTimer(
      guildId: $guildId
      name: $name
      gameTimer: $gameTimer
    ) {
      id
      name
      source
      startsAt
      endsAt
      durationSeconds
      active
      cancelledAt
      status
      canModify
    }
  }
`) as TypedDocumentNode<
  AddGuildNotificationTimerMutation,
  AddGuildNotificationTimerMutationVariables
>

export const ModifyGuildNotificationTimerDocument = parse(`
  mutation ModifyGuildNotificationTimer(
    $guildId: String!
    $timerId: String!
    $name: String
    $gameTimer: String
  ) {
    modifyGuildNotificationTimer(
      guildId: $guildId
      timerId: $timerId
      name: $name
      gameTimer: $gameTimer
    ) {
      id
      name
      source
      startsAt
      endsAt
      durationSeconds
      active
      cancelledAt
      status
      canModify
    }
  }
`) as TypedDocumentNode<
  ModifyGuildNotificationTimerMutation,
  ModifyGuildNotificationTimerMutationVariables
>

export const StartGuildNotificationTimerDocument = parse(`
  mutation StartGuildNotificationTimer(
    $guildId: String!
    $timerId: String!
    $gameTimer: String!
  ) {
    startGuildNotificationTimer(
      guildId: $guildId
      timerId: $timerId
      gameTimer: $gameTimer
    ) {
      id
      name
      source
      startsAt
      endsAt
      durationSeconds
      active
      cancelledAt
      status
      canModify
    }
  }
`) as TypedDocumentNode<
  StartGuildNotificationTimerMutation,
  StartGuildNotificationTimerMutationVariables
>

export const StopGuildNotificationTimerDocument = parse(`
  mutation StopGuildNotificationTimer($guildId: String!, $timerId: String!) {
    stopGuildNotificationTimer(guildId: $guildId, timerId: $timerId) {
      id
      name
      source
      startsAt
      endsAt
      durationSeconds
      active
      cancelledAt
      status
      canModify
    }
  }
`) as TypedDocumentNode<
  StopGuildNotificationTimerMutation,
  StopGuildNotificationTimerMutationVariables
>

export const RemoveGuildNotificationTimerDocument = parse(`
  mutation RemoveGuildNotificationTimer($guildId: String!, $timerId: String!) {
    removeGuildNotificationTimer(guildId: $guildId, timerId: $timerId) {
      guildId
      timerId
      removed
    }
  }
`) as TypedDocumentNode<
  RemoveGuildNotificationTimerMutation,
  RemoveGuildNotificationTimerMutationVariables
>

export const SetGuildNotificationTimerStatusChannelDocument = parse(`
  mutation SetGuildNotificationTimerStatusChannel(
    $guildId: String!
    $channelId: String
  ) {
    setGuildNotificationTimerStatusChannel(
      guildId: $guildId
      channelId: $channelId
    ) {
      guildId
      channelId
      messageId
    }
  }
`) as TypedDocumentNode<
  SetGuildNotificationTimerStatusChannelMutation,
  SetGuildNotificationTimerStatusChannelMutationVariables
>
